/* 一般外科 第五關 — 刀房：Critical View of Safety（第三關的黃先生，ERCP 後腹腔鏡膽囊切除；CVS、speak up、bail-out） */
import {
  addWait,
  annoy,
  ask,
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clearWait,
  clock,
  closeZoom,
  type Ctx,
  dither,
  doc,
  type DrawFn,
  type EndingStep,
  ent,
  eventBanner,
  findEnt,
  finding,
  flag,
  focusInput,
  type Hotspot,
  type Level,
  leaveBy,
  me,
  mkEnt,
  type MonitorData,
  note,
  obox,
  openMonitor,
  openZoom,
  P,
  pell,
  pline,
  playEnding,
  post,
  ptext,
  R,
  rebuildDyn,
  session,
  sessionGuard,
  spend,
  strList,
  T,
  tr,
  walkEnt,
  type ZoomCfg,
} from "../../engine";
import { sideMark } from "../shared/imaging";
import {
  type CamCmd,
  type CamCmdDef,
  type LapState,
  makeScope,
  newLap,
  type Tip,
  trackCmd,
} from "../shared/laparoscope";
import {
  contaminate,
  drawOrTable,
  gownChoice,
  guardTouch,
  scrubSinkDialog,
  type SterileVars,
  sterileStatus,
  type TimeoutItem,
  timeoutChoices,
} from "../shared/sterile";

/* ---------- static data ---------- */

const FACTS = {
  drive: tr(
    "下禮拜就想回去開車，車貸要繳，一天沒跑就少一天的錢",
    "Wants to get back to driving next week; he has a car loan to pay, and every day off the road is a day’s income lost",
  ),
  scar: tr(
    "聽說只有三個小洞，很怕被開一條大刀",
    "Heard it’s only three small holes; really scared of being cut open with a big incision",
  ),
  consent: tr(
    "同意書沒仔細看，不知道可能轉開腹、可能只切一部分、可能放引流管",
    "Didn’t read the consent form carefully; doesn’t know it might convert to open, might remove only part of the gallbladder, might leave a drain",
  ),
  ercp: tr(
    "上次 ERCP 從嘴巴插管子很難受，現在已經不黃了",
    "Last time’s ERCP, with the tube down his throat, was miserable; he’s not yellow anymore",
  ),
  fear: tr(
    "怕麻醉醒不過來，太太一個人顧不了家",
    "Afraid he won’t wake up from the anesthesia; his wife can’t keep the household going alone",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

/** Interactions that break sterility once gowned (or re-dirty scrubbed hands). PACS and the wall screen are look-only. */
const NONSTERILE = ["scrub", "anesth", "ormon", "tower", "kick", "board", "chart"];

type Target = "LIVER" | "GB" | "CALOT" | "ROUV" | "FALC" | "DUCT" | "BED" | "MORISON";
/** Camera targets in world coords (300×150; the patient's right is on the left of the screen). */
const TG: Record<Target, { x: number; y: number }> = {
  LIVER: { x: 160, y: 30 },
  GB: { x: 150, y: 52 },
  CALOT: { x: 114, y: 96 },
  ROUV: { x: 50, y: 86 },
  FALC: { x: 222, y: 40 },
  DUCT: { x: 116, y: 106 },
  BED: { x: 146, y: 56 },
  MORISON: { x: 64, y: 116 },
};

type Cmd = CamCmd<Target>;

/** Camera commands keyed by minutes since the scope went in (before 江主任 leaves). */
const CMDS: Record<number, CamCmdDef<Target>> = {
  2: { type: "look", tg: "LIVER", text: tr("先看一圈。肝臟表面。", "Look around first. Liver surface.") },
  4: { type: "look", tg: "GB", text: tr("看膽囊。", "Show me the gallbladder.") },
  6: {
    type: "out",
    text: tr("太近了，退一點，我要看整個右上腹。", "Too close. Back out a bit, I want the whole right upper quadrant."),
  },
  8: { type: "wipe", text: tr("起霧了，看不到。", "It’s fogged up. I can’t see.") },
  11: {
    type: "look",
    tg: "CALOT",
    minZ: 1.3,
    text: tr("靠近膽囊頸，我要開腹膜了。", "Get in close on the gallbladder neck. I’m opening the peritoneum."),
  },
  12: { type: "out", smoke: true, text: tr("煙，退一點。", "Smoke. Back out a bit.") },
  13: {
    type: "look",
    tg: "ROUV",
    text: tr(
      "往病人右邊、肝的下緣。Rouvière's sulcus 在哪？給我看。",
      "Toward the patient’s right, the lower edge of the liver. Where’s Rouvière’s sulcus? Show me.",
    ),
  },
};

/** Commands 阿凱 gives while 江主任 is away, keyed by crisis stage. */
const R_CMDS: Partial<Record<CrisisStage, CamCmdDef<Target>>> = {
  claim: {
    type: "look",
    tg: "DUCT",
    minZ: 1.4,
    who: "gs_r",
    text: tr("鏡頭靠近一點，給我看這條管子。", "Bring the camera in closer. Let me see this duct."),
  },
  ready: {
    type: "look",
    tg: "DUCT",
    minZ: 1.3,
    who: "gs_r",
    text: tr("穩住，我要夾了。", "Hold it steady. I’m clipping."),
  },
  bed: { type: "look", tg: "BED", who: "gs_r", text: tr("往上，給我看肝床。", "Up. Show me the liver bed.") },
};

const QS: Record<number, string> = {
  3: tr(
    "他上次來是膽管炎。Charcot triad 是哪三個？Reynolds pentad 多哪兩個？",
    "He came in last time with cholangitis. What are the three parts of Charcot’s triad? What two does Reynolds’ pentad add?",
  ),
  7: tr("Cystic artery 通常是從哪一條分出來的？", "What does the cystic artery usually come off?"),
  10: tr("Critical view of safety，三個條件是什麼？", "Critical view of safety. What are the three criteria?"),
  14: tr("Rouvière's sulcus 有什麼用？", "What is Rouvière’s sulcus good for?"),
};
const BAILQ = tr("CVS 拿不到的時候，你有哪些選擇？", "When you can’t get the CVS, what are your options?");
const QA: Record<string, string> = {
  [QS[3] ?? ""]: tr(
    "發燒、右上腹痛、黃疸；Reynolds 再加上低血壓（休克）與意識改變",
    "Fever, RUQ pain, jaundice; Reynolds adds hypotension (shock) and altered mental status",
  ),
  [QS[7] ?? ""]: tr(
    "右肝動脈（right hepatic artery），走在肝膽三角裡",
    "The right hepatic artery, running within the hepatocystic triangle",
  ),
  [QS[10] ?? ""]: tr(
    "一、肝膽三角裡的脂肪與纖維組織清乾淨；二、膽囊下三分之一從肝床（cystic plate）剝離；三、只看到兩條、而且只有兩條構造進入膽囊（cystic duct 與 cystic artery）",
    "One, the hepatocystic triangle cleared of fat and fibrous tissue; two, the lower third of the gallbladder taken off the liver bed (cystic plate); three, two and only two structures seen entering the gallbladder (the cystic duct and cystic artery)",
  ),
  [QS[14] ?? ""]: tr(
    "大約標出總膽管所在的平面；剝離要維持在 sulcus 的上方（腹側），往下就可能傷到總膽管",
    "It roughly marks the plane of the common bile duct; keep the dissection above the sulcus (ventral to it), because going below it risks injuring the CBD",
  ),
  [BAILQ]: tr(
    "停下來找主治、術中膽道攝影（IOC）、部分膽囊切除（subtotal cholecystectomy，fenestrating 或 reconstituting）、膽囊造口引流、轉開腹；不要硬夾看不清楚的構造",
    "Stop and call the attending, intraoperative cholangiogram (IOC), subtotal cholecystectomy (fenestrating or reconstituting), cholecystostomy drainage, conversion to open; never force a clip onto a structure you can’t clearly see",
  ),
};

/** Where the spilled gallstones can roll (world coords); four are picked per game. */
const STONE_SPOTS: { x: number; y: number; where: string }[] = [
  { x: 62, y: 118, where: tr("肝腎隱窩（Morison's pouch）", "in Morison’s pouch") },
  { x: 178, y: 112, where: tr("十二指腸旁邊", "next to the duodenum") },
  { x: 132, y: 136, where: tr("網膜上", "on the omentum") },
  { x: 88, y: 104, where: tr("膽囊頸下方", "below the gallbladder neck") },
  { x: 40, y: 98, where: tr("右側肝下緣", "under the right liver edge") },
  { x: 210, y: 92, where: tr("肝圓韌帶旁", "by the round ligament") },
];
const STONE_DECOYS = [
  { x: 150, y: 128, say: tr("那是脂肪球。", "That’s a fat globule.") },
  { x: 104, y: 122, say: tr("那是血塊。", "That’s a blood clot.") },
  { x: 196, y: 128, say: tr("那是十二指腸的反光。", "That’s glare off the duodenum.") },
  { x: 74, y: 132, say: tr("那是結腸肝彎。", "That’s the hepatic flexure of the colon.") },
];

/** Fluoroscopy hotspots for the IOC (close-up coords). */
const FLUORO_PARTS = {
  gb: { x: 52, y: 30, w: 30, h: 22, label: tr("膽囊（顯影劑從這裡打進去）", "Gallbladder (contrast goes in here)") },
  cystic: { x: 74, y: 52, w: 14, h: 10, label: tr("很短的一小段", "A very short segment") },
  cbd: { x: 88, y: 50, w: 14, h: 34, label: tr("粗的那條往下走", "The thick one running down") },
  chd: { x: 86, y: 18, w: 28, h: 30, label: tr("往上分岔的那幾條", "The branches going up") },
  duod: { x: 98, y: 84, w: 44, h: 18, label: tr("下面那團", "The mass at the bottom") },
} as const;
type FluoroPart = keyof typeof FLUORO_PARTS;
const FLUORO_NAME: Record<FluoroPart, string> = {
  gb: tr("膽囊", "gallbladder"),
  cystic: tr("膽囊管（cystic duct）", "cystic duct"),
  cbd: tr("總膽管（CBD）", "common bile duct (CBD)"),
  chd: tr("總肝管與肝內膽管", "common hepatic and intrahepatic ducts"),
  duod: tr("十二指腸", "duodenum"),
};

/* ---------- steps ---------- */

type Kind =
  | "idle"
  | "probe"
  | "fundus"
  | "adhesio"
  | "hartmann"
  | "peri"
  | "calot"
  | "clip"
  | "cut"
  | "bed"
  | "ioc"
  | "open"
  | "stones"
  | "suture"
  | "drain"
  | "bag";

const STEP = {
  ports: [
    tr("放 trocar、建立氣腹", "Ports & pneumoperitoneum"),
    "idle",
    tr(
      "肚臍用開放式放入 12 mm trocar，打氣到 12 mmHg；劍突下 10 mm、右上腹兩支 5 mm。",
      "A 12 mm trocar goes in at the umbilicus by open technique, insufflated to 12 mmHg; a 10 mm subxiphoid and two 5 mm in the right upper quadrant.",
    ),
  ],
  explore: [
    tr("探查", "Exploration"),
    "probe",
    tr(
      "肝臟表面光滑。膽囊被網膜和十二指腸包住，壁又厚又紅，是上次膽管炎留下來的發炎。",
      "The liver surface is smooth. The gallbladder is wrapped in omentum and duodenum, its wall thick and red: inflammation left over from the cholangitis.",
    ),
  ],
  fundus: [
    tr("抬起膽囊底", "Lift the fundus"),
    "fundus",
    tr(
      "阿凱夾住膽囊底，往頭側推過肝臟上緣。",
      "Kai grasps the fundus and pushes it cephalad over the top of the liver.",
    ),
  ],
  adhesio: [
    tr("剝開沾黏", "Take down adhesions"),
    "adhesio",
    tr(
      "主任一點一點把黏在膽囊上的網膜撥開。組織很水腫，碰一下就滲血。",
      "Dr. Chiang teases the omentum off the gallbladder bit by bit. The tissue is edematous and oozes at a touch.",
    ),
  ],
  hartmann: [
    tr("拉開膽囊頸", "Retract the neck"),
    "hartmann",
    tr(
      "主任左手夾住膽囊頸（Hartmann's pouch），往外下方拉，把肝膽三角撐開。",
      "With his left hand Dr. Chiang grasps the gallbladder neck (Hartmann’s pouch) and pulls it laterally and down, opening up the hepatocystic triangle.",
    ),
  ],
  peri: [
    tr("打開腹膜", "Open the peritoneum"),
    "peri",
    tr(
      "電燒鉤沿著膽囊頸兩側，把前葉、後葉腹膜打開。",
      "The hook cautery opens the anterior and posterior peritoneum along both sides of the gallbladder neck.",
    ),
  ],
  calot: [
    tr("剝離肝膽三角", "Dissect the triangle"),
    "calot",
    tr(
      "肝膽三角裡都是發炎過的脂肪和纖維，剝起來像在剝濕掉的紙板。主任剝得很慢。",
      "The hepatocystic triangle is all scarred fat and fibrous tissue; dissecting it is like peeling wet cardboard. Dr. Chiang goes very slowly.",
    ),
  ],
  rcalot: [
    tr("阿凱接手剝離", "Kai takes over"),
    "calot",
    tr(
      "阿凱換到主刀的位置，剝離的速度一下子快了起來。",
      "Kai moves into the surgeon’s spot, and the dissection suddenly speeds up.",
    ),
  ],
  clip: [tr("上 clip", "Clip"), "clip", tr("阿珍把 clip applier 遞給阿凱。", "Jen hands Kai the clip applier.")],
  cut: [tr("剪斷", "Cut"), "cut", tr("阿凱：「剪。」", "Kai: “Cut.”")],
  bed: [
    tr("剝離肝床", "Liver bed"),
    "bed",
    tr(
      "電燒鉤沿著膽囊和肝臟之間的平面，一段一段往上燒。",
      "The hook works its way up the plane between gallbladder and liver, one stretch at a time.",
    ),
  ],
  inspect: [
    tr("主任重新看", "Dr. Chiang re-checks"),
    "probe",
    tr(
      "主任接過器械，把膽囊頸往外拉，仔細看那條管子的上下。",
      "Dr. Chiang takes the instruments, pulls the gallbladder neck laterally, and looks carefully along that duct, above and below.",
    ),
  ],
  ioc: [
    tr("術中膽道攝影", "Cholangiogram"),
    "ioc",
    tr(
      "佩佩把 C-arm 推進來。主任用細針穿刺膽囊，打顯影劑。",
      "Pei-Pei wheels in the C-arm. Dr. Chiang punctures the gallbladder with a fine needle and injects contrast.",
    ),
  ],
  open: [
    tr("打開膽囊", "Open the gallbladder"),
    "open",
    tr(
      "主任用電燒在膽囊頸上方打開膽囊。膽汁和一顆顆結石湧了出來。",
      "Dr. Chiang opens the gallbladder above the neck with cautery. Bile and stone after stone spill out.",
    ),
  ],
  stones: [
    tr("撿結石", "Retrieve stones"),
    "stones",
    tr(
      "阿凱用吸引管吸膽汁，主任一顆一顆把結石夾進 Endobag。有幾顆滾開了。",
      "Kai suctions the bile while Dr. Chiang picks the stones one by one into an Endobag. A few roll away.",
    ),
  ],
  suture: [
    tr("從裡面縫合膽囊管開口", "Suture the cystic duct from inside"),
    "suture",
    tr(
      "主任從膽囊裡面找到膽囊管的開口，用可吸收線做荷包縫合。膽囊後壁留在肝床上。",
      "From inside the gallbladder Dr. Chiang finds the cystic duct opening and closes it with an absorbable purse-string. The back wall of the gallbladder stays on the liver bed.",
    ),
  ],
  cautery: [
    tr("燒灼殘餘黏膜", "Cauterize the mucosa"),
    "bed",
    tr(
      "留在肝床上的膽囊黏膜用電燒燒過一遍。",
      "The gallbladder mucosa left on the liver bed gets a pass with cautery.",
    ),
  ],
  drain: [
    tr("放引流管", "Place a drain"),
    "drain",
    tr(
      "一條引流管從右上腹的 trocar 傷口放進來，尖端擺在肝下。",
      "A drain goes in through the right upper quadrant trocar site, its tip under the liver.",
    ),
  ],
  bag: [
    tr("裝進 Endobag", "Into the Endobag"),
    "bag",
    tr(
      "膽囊和結石都裝進 Endobag，袋口收緊。",
      "Gallbladder and stones go into the Endobag, and the mouth is cinched shut.",
    ),
  ],
  extract: [
    tr("取出檢體", "Extract the specimen"),
    "bag",
    tr(
      "從肚臍的 trocar 把 Endobag 拉出來。袋子卡住，阿凱把筋膜切口擴大了一點。",
      "The Endobag comes out through the umbilical trocar. It catches, and Kai widens the fascial incision a little.",
    ),
  ],
  convert: [
    tr("轉開腹", "Convert to open"),
    "idle",
    tr(
      "主任：「轉開腹。」右肋下切開，阿珍飛快地換上開腹器械。",
      "Dr. Chiang: “Converting to open.” A right subcostal incision; Jen swaps in the open instruments at speed.",
    ),
  ],
} satisfies Record<string, [name: string, kind: Kind, narr: string]>;
type StepId = keyof typeof STEP;

/* ---------- session ---------- */

type Phase = "preop" | "timeout" | "lap" | "close" | "post";
type Judge = "correct" | "partial" | "wrong" | "none" | "skipped";
const isGrade = (x: unknown): x is "correct" | "partial" | "wrong" =>
  x === "correct" || x === "partial" || x === "wrong";
type Stop = "precise" | "vague" | "none";
const isStop = (x: unknown): x is Stop => x === "precise" || x === "vague" || x === "none";

/** 阿凱's march towards clipping while 江主任 is away. */
type CrisisStage = "claim" | "ready" | "clip1" | "clip2" | "cut" | "bed" | "done";
const CRISIS_NEXT: Record<CrisisStage, [next: CrisisStage, gap: number]> = {
  claim: ["ready", 2],
  ready: ["clip1", 3],
  clip1: ["clip2", 1],
  clip2: ["cut", 1],
  cut: ["bed", 2],
  bed: ["done", 0],
  done: ["done", 0],
};

/** How the near miss ended. */
type Path = "safe" | "cutKnown" | "cutMissed";

interface Vars extends SterileVars {
  phase: Phase;
  step: StepId | null;
  seq: { at: number; id: StepId | "hunt" | "count" | "close" | "post" | "iocAsk" | "bailQ" }[];
  asleep: boolean;
  draped: boolean;
  lapAt: number | null;
  timeout: { done: boolean; withClerk: boolean; ans: Record<string, string> };
  cmd: Cmd | null;
  cam: { hit: number; miss: number; wrong: number };
  q: string | null;
  qAt: number | null;
  qRes: { q: string; j: Judge }[];
  trust: number;
  qToR: boolean;
  rAnswer: { t: number; a: string } | null;
  offerSuture: boolean;
  sutured: boolean;
  revealed: Set<FactId>;
  explained: boolean;
  anesAsked: boolean;
  lap: LapState;
  autoTg: Target | null;
  vsAway: boolean;
  awayAt: number | null;
  vsDue: number | null;
  backAt: number | null;
  crisis: { stage: CrisisStage; at: number } | null;
  stopBy: "precise" | "escalate" | null;
  stopAt: number | null;
  vagueN: number;
  holdUsed: boolean;
  stopLines: string[];
  calledVs: number | null;
  ductLook: boolean;
  clipCBD: boolean;
  clipRemoved: boolean;
  cbdCut: boolean;
  recognized: boolean;
  recognizedAt: number | null;
  recognizedBy: string | null;
  path: Path | null;
  ioc: { shown: boolean; ask: FluoroPart | null; picks: string[] };
  gbOpen: boolean;
  gbGone: boolean;
  stones: { x: number; y: number; where: string }[];
  hunt: { on: boolean; until: number | null; picks: string[]; found: number; byR: number };
  drain: boolean;
  bagged: boolean;
  chartRead: boolean;
  pacsRead: boolean;
  closeAt: number | null;
  postAt: number | null;
  wifePre: number;
  wifePost: number;
  overpromise: boolean;
  blamed: boolean;
  lied: boolean;
  endQueued: boolean;
  tableAnim: { t0: number } | null;
  tableGone: boolean;
  warnedTable: string;
  lastMissAt: number;
}

const S = session<Vars>();
const DOOR: Cell = [13, 11];
const VS_SPOT: Cell = [14, 5];
const R_SPOT: Cell = [12, 5];

const inOR = (): boolean => P.x >= 9;
const rel = (t: number = S.t): number => t - (S.lapAt ?? t);
const stepInfo = () => (S.step ? STEP[S.step] : undefined);
const kindNow = (): Kind => stepInfo()?.[1] ?? "idle";
/** Who is operating right now (asks questions, gives camera commands). */
const lead = (): string => (S.vsAway ? "gs_r" : "gs_vs");
const inLap = (): boolean => S.phase === "lap";
/** Spilled stones nobody found (only meaningful after the hunt). */
const leftStones = (): number => (S.path === "safe" && S.hunt.until !== null && !S.hunt.on ? S.stones.length : 0);
const pathName = (): string =>
  S.path === "cutKnown"
    ? tr("總膽管剪斷，術中發現", "CBD transected, recognized intraop")
    : S.path === "cutMissed"
      ? tr("總膽管剪斷，沒有發現", "CBD transected, not recognized")
      : S.clipCBD
        ? tr("總膽管被夾一個 clip 後拿掉", "One clip on the CBD, then removed")
        : S.stopBy === "precise"
          ? tr("clerk 說出理由喊停", "Clerk called a stop and gave reasons")
          : S.stopBy === "escalate"
            ? tr("找主任回來", "Called the attending back")
            : tr("尚未發生", "Not yet");

/* ---------- step runner ---------- */

/** Camera asks 江主任 makes during the later steps. */
const STEP_CMDS: Partial<Record<StepId, CamCmdDef<Target>>> = {
  open: { type: "out", smoke: true, text: tr("煙，退一點。", "Smoke. Back out a bit.") },
  suture: {
    type: "look",
    tg: "CALOT",
    minZ: 1.3,
    text: tr("靠近膽囊頸，我要縫了。", "Get in close on the gallbladder neck. I’m going to suture."),
  },
  drain: {
    type: "look",
    tg: "MORISON",
    text: tr("往肝下面，Morison's pouch。引流管放這裡。", "Under the liver, Morison’s pouch. The drain goes here."),
  },
  bed: { type: "look", tg: "BED", text: tr("往上，肝床。", "Up. Liver bed.") },
};

function setStep(id: StepId): void {
  S.step = id;
  post("table", "narr", null, STEP[id][2]);
  const sc = STEP_CMDS[id];
  if (sc && !S.vsAway && S.phase === "lap") issueCmd(sc);
  if (id === "adhesio")
    finding(
      tr("腹腔鏡", "Laparoscopy"),
      tr(
        "膽囊壁增厚、充血水腫，被網膜與十二指腸沾黏；肝膽三角纖維化。",
        "Thickened, hyperemic, edematous gallbladder wall with adhesions to omentum and duodenum; hepatocystic triangle fibrotic.",
      ),
    );
  if (id === "ioc") S.autoTg = "GB";
  if (id === "open") {
    S.gbOpen = true;
    S.lap.smoke = Math.max(S.lap.smoke, 0.3);
  }
  if (id === "drain") S.drain = true;
  if (id === "bag") S.bagged = true;
  if (id === "extract") S.gbGone = true;
  if (id === "convert") {
    S.lap.fog = 0;
    closeZoom();
  }
}

function runSeq(list: [delay: number, id: Vars["seq"][number]["id"]][]): void {
  S.seq = list.map(([d, id]) => ({ at: S.t + d, id }));
}

/* ---------- laparoscope ---------- */

const scope = makeScope({
  lap: () => S.lap,
  worldKey: () =>
    [
      S.step,
      S.clipCBD,
      S.clipRemoved,
      S.cbdCut,
      S.gbOpen,
      S.gbGone,
      S.drain,
      S.stones.length,
      S.hunt.on,
      S.path,
      S.ioc.shown,
    ].join("|"),
  paintWorld,
  // The cystic artery is a one-pixel line: keep it red.
  textureKeep: ["#7a2028", "#c8505a"],
  tips: toolTips,
  overlay(c, now, toS) {
    // A blinking bead of bile once the duct has been cut.
    if (S.cbdCut && !S.gbGone && Math.floor(now / 400) % 2 === 0) {
      const [x, y] = toS(TG.DUCT.x, TG.DUCT.y + 4);
      R(c, "#b8d040", x - 1, y, 3, 2);
    }
  },
  hud(c) {
    if (S.vsAway) ptext(c, "VS OUT", 150, 100, "#ff8a8a");
  },
  caption() {
    const st = stepInfo();
    return st
      ? tr(
          `${S.vsAway ? "阿凱主刀" : "江主任主刀"}：${st[0]}`,
          `${S.vsAway ? "Kai" : "Dr. Chiang"} operating: ${st[0]}`,
        )
      : tr("你扶著鏡頭", "You’re on the camera");
  },
  hot: lapHot,
  onFrame(dt) {
    evalCmd(dt);
    if (!S.atCamera && S.autoTg) {
      const tg = TG[S.autoTg];
      S.lap.cx += (tg.x - S.lap.cx) * Math.min(1, dt * 1.5);
      S.lap.cy += (tg.y - S.lap.cy) * Math.min(1, dt * 1.5);
    }
    if (S.atCamera && !S.ductLook && S.vsAway && !S.cbdCut) {
      const near = Math.hypot(S.lap.cx - TG.DUCT.x, S.lap.cy - TG.DUCT.y) < 18 && S.lap.z >= 1.35;
      if (near && S.lap.fog < 0.5) {
        S.ductLook = true;
        const f = tr(
          "鏡頭拉近：那條「管子」很粗，比旁邊的血管粗好幾倍，往下一路延伸進十二指腸後面；三角裡還都是脂肪，膽囊下段也還黏在肝床上。進到膽囊的構造，你只看得到這一條。",
          "Up close: that “duct” is thick, several times the size of the vessel beside it, and it runs all the way down behind the duodenum; the triangle is still all fat, and the lower gallbladder is still stuck to the liver bed. Of the structures going into the gallbladder, this is the only one you can see.",
        );
        finding(tr("鏡頭所見", "Camera view"), f);
        post("table", "narr", null, f);
      }
    }
  },
  onWipe() {
    post(
      "table",
      "narr",
      null,
      tr("你把鏡頭抽出來擦乾淨，再放回去。", "You pull the scope out, wipe it clean, and put it back in."),
      { silent: true },
    );
    if (S.cmd?.type === "wipe") S.cmd.wiped = true;
  },
  extraBar: () =>
    S.vsAway && !S.cbdCut && !S.stopBy && !S.holdUsed ? [C(tr("喊停", "Stop!"), holdOn, false, "warn")] : [],
});

function paintWorld(c: Ctx): void {
  const E = (x: number, y: number, rx: number, ry: number, col: string) => pell(c, x, y, rx, ry, col);
  // Peritoneal cavity backdrop.
  c.fillStyle = "#24090e";
  c.fillRect(0, 0, 300, 150);
  E(150, 112, 150, 58, "#36111a");
  dither(c, 0, 60, 300, 90, "#2e0e14", 0.25);
  // Colon (hepatic flexure) lower left, omentum lower middle, duodenum lower right.
  E(48, 136, 46, 16, "#b8907a");
  E(48, 134, 44, 13, "#d8b49a");
  for (let i = 0; i < 4; i++) R(c, "#b8907a", 18 + i * 18, 126, 1, 18);
  E(130, 142, 52, 14, "#d8b860");
  dither(c, 82, 130, 100, 20, "#e8cc78", 0.4);
  E(186, 124, 40, 14, "#b86e70");
  E(186, 122, 38, 12, "#e09a98");
  E(176, 118, 12, 4, "#f0bcb4");
  // Liver: right lobe hangs lower on the screen-left (the patient's right).
  E(150, 8, 168, 58, "#4a1810");
  E(150, 6, 166, 56, "#9a3a2c");
  E(56, 50, 62, 44, "#4a1810");
  E(56, 48, 60, 42, "#9a3a2c");
  E(150, 50, 140, 10, "#8a3226");
  E(120, 20, 60, 16, "#b24c3a");
  E(70, 36, 22, 9, "#b24c3a");
  E(96, 14, 14, 4, "#c86a52");
  dither(c, 0, 0, 300, 50, "#7a2a22", 0.08);
  // Rouvière's sulcus: a dark cleft on the underside of the right lobe.
  pline(c, 34, 80, 64, 88, "#3a0e0c", 3);
  pline(c, 36, 79, 62, 86, "#5a1a14", 1);
  // Falciform ligament / ligamentum teres on the patient's left.
  pline(c, 222, 0, 214, 64, "#c8b070", 6);
  pline(c, 222, 0, 214, 64, "#ecd898", 3);
  E(214, 66, 6, 4, "#e8d088");

  if (S.path === "cutKnown" && S.step === "convert") {
    // Converted: the field is now an open wound edge; keep the anatomy but add retractor blades.
    R(c, "#c9ccd8", 0, 0, 300, 6);
    R(c, "#9aa0b2", 0, 6, 300, 2);
  }

  // Gallbladder bed once the gallbladder is out.
  if (S.gbGone) {
    E(150, 54, 30, 9, "#6a1e18");
    E(150, 54, 28, 7, "#9a3a2e");
    dither(c, 122, 48, 56, 12, "#c06a4a", 0.35);
    if (S.path === "safe") {
      // Subtotal: the back wall is left on the liver, cauterised.
      E(130, 70, 18, 8, "#7a6a50");
      dither(c, 112, 64, 36, 12, "#4a3a28", 0.5);
    }
  } else {
    // Gallbladder: fundus pushed up over the liver, neck pulled out and down.
    const up = S.step !== null && S.step !== "ports" && S.step !== "explore";
    const fx = up ? 182 : 162;
    const fy = up ? 30 : 62;
    const hx = 102;
    const hy = 86;
    const seg: [x: number, y: number, r: number][] = [];
    for (let i = 0; i <= 14; i++) {
      const f = i / 14;
      seg.push([fx + (hx - fx) * f, fy + (hy - fy) * f, 14 - 5 * f]);
    }
    for (const [x, y, r] of seg) E(x, y, r + 1, r * 0.8 + 1, "#4a5038");
    for (const [x, y, r] of seg) E(x, y, r, r * 0.8, S.gbOpen ? "#7a8660" : "#98a47c");
    for (const [x, y, r] of seg.filter((_, i) => i % 3 === 0)) E(x - 2, y - 2, r * 0.45, r * 0.25, "#bcc89c");
    // Hyperaemic, oedematous wall from the cholangitis.
    for (const [x, y] of [
      [164, 44],
      [140, 60],
      [118, 76],
      [172, 38],
    ] as Cell[])
      E(x, y, 4, 2, "#c07264");
    if (S.gbOpen) {
      // Opened neck: dark interior, bile.
      E(hx + 4, hy - 6, 8, 5, "#2a2a12");
      E(hx + 4, hy - 6, 6, 3, "#6a8a20");
    }
  }

  // Omental adhesions until 江主任 takes them down.
  const cleared = S.step !== null && !["ports", "explore", "fundus"].includes(S.step);
  if (!cleared) {
    for (const [x, y, rx] of [
      [108, 92, 16],
      [130, 78, 14],
      [92, 104, 12],
      [150, 66, 12],
    ] as [number, number, number][]) {
      E(x, y, rx, rx * 0.6, "#c8a050");
      E(x - 2, y - 1, rx - 2, rx * 0.5, "#e8c870");
    }
  }

  // Hepatocystic triangle: fatty and fibrotic until it is dissected.
  const dissected = S.step !== null && !["ports", "explore", "fundus", "adhesio", "hartmann", "peri"].includes(S.step);
  const sawAnatomy = S.ioc.shown || S.gbOpen || S.path === "cutKnown";

  // The common bile duct, tented up towards the gallbladder neck by the traction: it looks like the cystic duct.
  const duct: Cell[] = [
    [134, 64],
    [124, 82],
    [114, 98],
    [116, 112],
    [126, 132],
  ];
  if (dissected || S.gbGone) {
    for (let i = 0; i < duct.length - 1; i++) {
      const a = duct[i] as Cell;
      const b = duct[i + 1] as Cell;
      if (S.cbdCut && i === 2) {
        pline(c, a[0], a[1], 115, 105, "#6a7a50", 7);
        pline(c, a[0], a[1], 115, 105, "#cfd8b0", 5);
        continue;
      }
      pline(c, a[0], a[1], b[0], b[1], "#6a7a50", 7);
      pline(c, a[0], a[1], b[0], b[1], "#cfd8b0", 5);
      pline(c, a[0] - 1, a[1], b[0] - 1, b[1], "#e8ecd0", 1);
    }
    if (S.cbdCut) {
      // Transected: two stumps and bile pooling.
      E(116, 110, 8, 5, "#7a9a2a");
      dither(c, 104, 104, 26, 14, "#a8c040", 0.4);
    }
  }
  if (!dissected && !S.gbGone) {
    E(116, 98, 18, 12, "#c0a058");
    dither(c, 98, 86, 36, 26, "#e0c070", 0.45);
    for (let i = 0; i < 4; i++) pline(c, 102 + i * 7, 90, 108 + i * 6, 110, "#e8e0d0", 1);
  } else if (!S.gbGone) {
    // Partially dissected: fat still hugging the neck and the liver bed — no critical view.
    E(106, 90, 9, 6, "#c8a050");
    E(130, 72, 10, 5, "#c8a050");
    dither(c, 96, 82, 44, 18, "#e0c070", 0.3);
  }

  // The real cystic duct: short, low and running alongside the CBD — only seen after IOC / opening.
  if (sawAnatomy && !S.gbGone) {
    pline(c, 102, 94, 114, 102, "#6a7a50", 4);
    pline(c, 102, 94, 114, 102, "#c8d0a0", 2);
  }
  // Cystic artery, once 江主任 has cleaned the triangle (safe path) or before the bed dissection.
  if (sawAnatomy && !S.gbGone) {
    pline(c, 136, 70, 112, 84, "#7a2028", 2);
    pline(c, 136, 70, 112, 84, "#c8505a", 1);
  }

  // Clips.
  const clip = (x: number, y: number) => {
    R(c, "#4a4a52", x - 3, y - 1, 7, 3);
    R(c, "#e8d070", x - 3, y, 7, 1);
  };
  if (S.clipCBD && !S.clipRemoved) {
    clip(115, 103);
    if (S.cbdCut || S.crisis?.stage === "cut" || S.crisis?.stage === "bed" || S.crisis?.stage === "done")
      clip(117, 109);
  }
  if (S.path === "cutMissed" && S.gbGone) {
    clip(115, 101);
    clip(117, 112);
  }
  if (S.path === "safe" && S.gbGone) {
    // Purse-string on the cystic duct opening.
    pline(c, 106, 92, 112, 96, "#7a5ab8", 1);
    pline(c, 106, 96, 112, 92, "#7a5ab8", 1);
  }

  // Spilled stones.
  if (S.path === "safe" && S.gbOpen) {
    for (const st of S.stones) {
      E(st.x, st.y, 4, 3, "#2a2414");
      E(st.x, st.y, 3, 2, "#c8a040");
      R(c, "#f0d890", st.x - 1, st.y - 1, 1, 1);
    }
    if (S.hunt.on)
      for (const d of STONE_DECOYS) {
        E(d.x, d.y, 4, 3, "#c89a50");
        E(d.x, d.y, 3, 2, "#e8c070");
      }
  }

  // Drain.
  if (S.drain) {
    pline(c, 0, 108, 90, 112, "#d9dcea", 4);
    pline(c, 0, 108, 90, 112, "#ffffff", 2);
    for (let i = 10; i < 90; i += 12) R(c, "#5a6070", i, 109, 2, 2);
  }
}

function toolTips(now: number): { L: Tip; R: Tip } | null {
  const k = kindNow();
  const tt = now / 1000;
  const idle: { L: Tip; R: Tip } = {
    L: { x: 104, y: 92, kind: "grasp", open: false },
    R: { x: 172, y: 92, kind: "grasp", open: false },
  };
  if (S.phase !== "lap" || S.step === "convert") return null;
  const neck: Tip = { x: 100, y: 86, kind: "grasp", hold: true };
  switch (k) {
    case "probe":
      return {
        L: { x: 120 + 18 * Math.sin(tt * 0.9), y: 70 + 6 * Math.cos(tt * 1.3), kind: "grasp", open: tt % 2 < 1 },
        R: idle.R,
      };
    case "fundus":
      return { L: { x: 186, y: 30 + Math.sin(tt * 2), kind: "grasp", hold: true }, R: idle.R };
    case "adhesio":
      return {
        L: neck,
        R: {
          x: 118 + 14 * Math.sin(tt * 0.6),
          y: 82 + 6 * Math.cos(tt * 0.8),
          kind: "hook",
          spark: Math.floor(now / 200) % 3 === 0,
        },
      };
    case "hartmann":
      return { L: { x: 96 + Math.sin(tt * 2), y: 88, kind: "grasp", hold: true }, R: idle.R };
    case "peri":
      return {
        L: neck,
        R: {
          x: 112 + 8 * Math.sin(tt * 0.7),
          y: 90 + 4 * Math.cos(tt),
          kind: "hook",
          spark: Math.floor(now / 180) % 3 === 0,
        },
      };
    case "calot":
      return {
        L: neck,
        R: {
          x: 114 + 4 * Math.sin(tt * 1.4),
          y: 100 + 3 * Math.cos(tt * 1.1),
          kind: "grasp",
          open: Math.floor(tt * 2) % 2 === 0,
        },
      };
    case "clip": {
      const closing = S.crisis?.stage === "clip1" || S.crisis?.stage === "clip2" || S.clipCBD;
      return { L: neck, R: { x: 117, y: 105, kind: "clip", open: !closing && Math.floor(tt * 2) % 2 === 0 } };
    }
    case "cut":
      return { L: neck, R: { x: 115, y: 106, kind: "scissors", open: Math.floor(tt * 3) % 2 === 0 } };
    case "bed":
      return {
        L: { x: 160, y: 40, kind: "grasp", hold: true },
        R: { x: 140 + 10 * Math.sin(tt * 0.5), y: 58, kind: "hook", spark: Math.floor(now / 180) % 3 === 0 },
      };
    case "ioc":
      return { L: neck, R: { x: 150, y: 54, kind: "grasp", open: false } };
    case "open":
      return { L: neck, R: { x: 106, y: 80, kind: "hook", spark: Math.floor(now / 160) % 2 === 0 } };
    case "stones":
      return {
        L: { x: 110 + 20 * Math.sin(tt * 0.8), y: 112 + 6 * Math.cos(tt), kind: "suction" },
        R: { x: 96, y: 98, kind: "grasp", open: Math.floor(tt * 2) % 2 === 0 },
      };
    case "suture":
      return {
        L: { x: 104, y: 90 + 2 * Math.sin(tt * 3), kind: "grasp", hold: true },
        R: { x: 110 + 3 * Math.sin(tt * 2), y: 94, kind: "grasp", open: Math.floor(tt * 2) % 2 === 0 },
      };
    case "drain":
      return { L: { x: 88 - 20 * ((tt % 3) / 3), y: 112, kind: "grasp", hold: true }, R: idle.R };
    case "bag": {
      const f = S.step === "extract" ? (tt % 4) / 4 : 0;
      return {
        L: idle.L,
        R: { x: 128 + 40 * f, y: 110 + 10 * f, kind: "bag", specimen: true },
      };
    }
    default:
      return idle;
  }
}

function lapHot(): Hotspot[] {
  if (!S.hunt.on || !S.atCamera) return [];
  const { toS, inView } = scope;
  const hs: Hotspot[] = [];
  S.stones.forEach((st, i) => {
    const p = toS(st.x, st.y);
    if (inView(p))
      hs.push({
        x: p[0] - 8,
        y: p[1] - 7,
        w: 16,
        h: 14,
        label: tr("指給主任：這裡有一顆", "Show Dr. Chiang: a stone here"),
        fn: () => pick(i),
      });
  });
  STONE_DECOYS.forEach((d, i) => {
    const p = toS(d.x, d.y);
    if (inView(p))
      hs.push({
        x: p[0] - 8,
        y: p[1] - 7,
        w: 16,
        h: 14,
        label: tr("指給主任：這裡有一顆", "Show Dr. Chiang: a stone here"),
        fn: () => pick(-1 - i),
      });
  });
  hs.push({
    x: 0,
    y: 0,
    w: 192,
    h: 108,
    label: tr("指給主任：這裡有一顆", "Show Dr. Chiang: a stone here"),
    fn: () => pick(null),
  });
  return hs;
}

function pick(i: number | null): void {
  if (S.lap.fog > 0.5) {
    post(
      "table",
      "npc",
      "gs_vs",
      tr("霧成這樣你看得到什麼？先擦鏡頭。", "In that fog, what can you see? Wipe the scope first."),
    );
    return;
  }
  if (i !== null && i >= 0) {
    const st = S.stones[i];
    if (!st) return;
    if (S.lap.z < 1.25) {
      post(
        "table",
        "npc",
        "gs_vs",
        tr("那邊好像有東西……太遠了，拉近一點。", "Something over there, maybe… Too far. Get closer."),
      );
      return;
    }
    S.stones.splice(i, 1);
    S.hunt.found++;
    S.hunt.picks.push(tr("找到", "found"));
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        `好，${st.where}一顆。夾起來。${S.stones.length ? `還有 ${S.stones.length} 顆。` : ""}`,
        `Good, one ${st.where}. Grab it.${S.stones.length ? ` ${S.stones.length} more.` : ""}`,
      ),
    );
    if (!S.stones.length) endHunt("clerk");
    return;
  }
  if (i !== null) {
    S.hunt.picks.push(tr("誤認", "decoy"));
    post("table", "npc", "gs_vs", (STONE_DECOYS[-1 - i]?.say ?? "") + tr("再找。", " Keep looking."));
    return;
  }
  if (S.t - S.lastMissAt >= 1) {
    S.lastMissAt = S.t;
    S.hunt.picks.push(tr("空", "empty"));
    post("table", "npc", "gs_vs", tr("那邊沒東西。", "Nothing there."));
  }
}

function evalCmd(dt: number): void {
  const cmd = S.cmd;
  if (!cmd || !S.atCamera) return;
  const decoy: Target | null = cmd.tg === "ROUV" ? "FALC" : null;
  const { hit, wrong } = trackCmd(cmd, S.lap, dt, TG, decoy);
  if (wrong) {
    S.cam.wrong++;
    post(
      "table",
      "npc",
      "gs_r",
      tr(
        "學弟，病人的右邊在畫面左邊啦。那是鐮狀韌帶。",
        "Kid, the patient’s right is on the left of the screen. That’s the falciform ligament.",
      ),
    );
  }
  if (hit) {
    S.cam.hit++;
    S.cmd = null;
    clearWait("cam");
    flag(cmd.who ?? "gs_vs");
    post(
      "table",
      "npc",
      cmd.who ?? "gs_vs",
      tr(["好。", "嗯。", "對。", "好，就這樣。"], ["Good.", "Mm.", "Right.", "Good, like that."])[S.cam.hit % 4] ??
        tr("好。", "Good."),
    );
  }
}

function issueCmd(def: CamCmdDef<Target>): void {
  const who = def.who ?? "gs_vs";
  S.autoTg = def.tg ?? S.autoTg;
  if (S.atCamera) {
    if (S.cmd) S.cam.miss++;
    S.cmd = { ...def, t: S.t };
    if (def.smoke) S.lap.smoke = 0.9;
    post("table", "npc", who, def.text);
    addWait({ id: "cam", text: tr("鏡頭：", "Camera: ") + def.text, untilT: S.t + 3, clearOn: "manual", icon: "◎" });
  } else
    post(
      "table",
      "narr",
      null,
      tr(
        `${who === "gs_vs" ? "江主任" : "阿凱"}：「${def.text}」阿珍幫忙調整了鏡頭。`,
        `${who === "gs_vs" ? "Dr. Chiang" : "Kai"}: “${def.text}” Jen adjusts the camera.`,
      ),
    );
}

/* ---------- questions ---------- */

function askQ(q: string): void {
  if (S.q) qMiss();
  if (S.qToR) {
    post("table", "npc", "gs_vs", tr("阿凱，" + q, "Kai. " + q));
    S.rAnswer = { t: S.t + 1, a: (QA[q] ?? "") + tr("。", ".") };
    S.qRes.push({ q, j: "skipped" });
    return;
  }
  S.q = q;
  S.qAt = S.t;
  ask("table", "gs_vs", (S.sterile ? "" : tr("外面那個 clerk，", "You, the clerk standing back there. ")) + q, {
    id: "q",
    text: tr("江主任在等你回答", "Dr. Chiang is waiting for your answer"),
    dlg: "table",
    clearOn: "manual",
  });
}

function qMiss(): void {
  if (!S.q) return;
  const q = S.q;
  S.q = null;
  clearWait("q");
  flag("gs_vs");
  S.qRes.push({ q, j: "none" });
  S.trust--;
  S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
  post(
    "table",
    "npc",
    "gs_vs",
    tr(
      "問你話不回？" + (QA[q] ?? "") + "。回去查。",
      "Not answering when I ask you? " + (QA[q] ?? "") + ". Go look it up.",
    ),
  );
  trustCheck();
}

function judged(j: Judge): void {
  if (j === "correct") S.trust++;
  else if (j === "wrong") S.trust--;
  trustCheck();
}

function trustCheck(): void {
  if (S.trust <= -2 && !S.qToR) {
    S.qToR = true;
    post(
      "table",
      "npc",
      "gs_vs",
      tr("算了。接下來的問題阿凱回答。", "Forget it. Kai answers the questions from here on."),
    );
  }
  if (S.trust >= 2 && !S.offerSuture) {
    S.offerSuture = true;
    post("table", "npc", "gs_vs", tr("不錯。等一下關傷口，皮你來縫。", "Not bad. When we close, you do the skin."));
  }
}

/* ---------- the near miss ---------- */

function vsLeaves(): void {
  S.vsAway = true;
  S.awayAt = S.t;
  S.vsDue = S.t + 12;
  if (S.q) {
    clearWait("q");
    S.q = null;
  }
  S.cmd = null;
  clearWait("cam");
  eventBanner(tr("主任被叫走", "Attending called away"), tr("6 房出血", "Bleeding in OR 6"), true);
  post(
    "table",
    "alert",
    "circ",
    tr(
      "江主任！6 房打來，膽囊切除那台在出血，請你馬上過去！",
      "Dr. Chiang! OR 6 is calling. Their cholecystectomy is bleeding, they need you right now!",
    ),
  );
  post(
    "table",
    "npc",
    "gs_vs",
    tr(
      "阿凱，你繼續剝，Calot 弄乾淨一點。我很快回來。",
      "Kai, keep dissecting. Clean up Calot’s a bit more. I’ll be right back.",
    ),
  );
  const v = ent("gs_vs");
  v.working = false;
  leaveBy(v, DOOR);
  const alive = sessionGuard();
  setTimeout(() => {
    if (!alive()) return;
    const r = ent("gs_r");
    walkEnt(r, VS_SPOT);
    r.onArrive = () => {
      r.face = "left";
      r.working = true;
    };
  }, 1500);
  S.crisis = { stage: "claim", at: S.t + 2 };
  setStep("rcalot");
  post(
    "table",
    "npc",
    "gs_r",
    tr(
      "主任那邊不知道要多久……下一台還在等。我們加快一點。",
      "No telling how long he’ll be over there… The next case is waiting. Let’s pick up the pace.",
    ),
  );
}

function crisisStep(): void {
  const cr = S.crisis;
  if (!cr || S.stopBy || S.t < cr.at) return;
  const st = cr.stage;
  if (st === "claim") {
    post("table", "npc", "gs_r", tr("看，這條就是 cystic duct，很明顯嘛。", "See, this is the cystic duct. Obvious."));
    const cmd = R_CMDS.claim;
    if (cmd) issueCmd(cmd);
  } else if (st === "ready") {
    setStep("clip");
    post("table", "npc", "gs_r", tr("Clip。", "Clip."));
    post(
      "table",
      "npc",
      "scrub",
      tr(
        "……Clip applier。主任說等他回來看嗎？",
        "…Clip applier. Didn’t Dr. Chiang say to wait till he’s back to look?",
      ),
    );
    post("table", "npc", "gs_r", tr("不用啦，這條很清楚。", "Nah, this one’s clear."));
    const cmd = R_CMDS.ready;
    if (cmd) issueCmd(cmd);
    addWait({
      id: "clipping",
      text: tr("阿凱要上 clip 了", "Kai is about to clip"),
      untilT: S.t + CRISIS_NEXT.ready[1],
      urgent: true,
      icon: tr("！", "!"),
      clearOn: "manual",
    });
  } else if (st === "clip1") {
    S.clipCBD = true;
    clearWait("clipping");
    post("table", "narr", null, tr("喀。第一個 clip 夾在那條管子上。", "Click. The first clip goes on that duct."));
  } else if (st === "clip2") {
    post(
      "table",
      "narr",
      null,
      tr("喀。第二個 clip。阿凱伸手要剪刀。", "Click. Second clip. Kai reaches for the scissors."),
    );
    addWait({
      id: "cutting",
      text: tr("阿凱要剪了", "Kai is about to cut"),
      untilT: S.t + 1,
      urgent: true,
      icon: tr("！", "!"),
      clearOn: "manual",
    });
  } else if (st === "cut") {
    setStep("cut");
    S.cbdCut = true;
    clearWait("cutting");
    post(
      "table",
      "narr",
      null,
      tr(
        "剪刀合上。管子斷成兩截，斷面滲出一點金黃色的液體。阿凱用紗布壓了一下，沒有多看。",
        "The scissors close. The duct falls in two, and a little golden fluid oozes from the cut end. Kai dabs it with gauze and doesn’t look twice.",
      ),
    );
    finding(
      tr("術中", "Intraop"),
      tr(
        "阿凱在 CVS 未達成的情況下夾斷了一條構造（實為總膽管）。",
        "Kai clipped and divided a structure (actually the CBD) without the CVS achieved.",
      ),
    );
    S.path = "cutMissed";
  } else if (st === "bed") {
    setStep("bed");
    post("table", "npc", "gs_r", tr("Artery 也夾了，接下來剝肝床。", "Artery’s clipped too. Now the liver bed."));
    const cmd = R_CMDS.bed;
    if (cmd) issueCmd(cmd);
  }
  const [next, gap] = CRISIS_NEXT[st];
  cr.stage = next;
  cr.at = S.t + gap;
}

/** The 喊停 button: buys a minute and makes the clerk say why. */
function holdOn(): void {
  if (S.holdUsed || S.stopBy || S.cbdCut) return;
  S.holdUsed = true;
  me("table", tr("等一下！", "Wait!"));
  post("table", "npc", "gs_r", tr("……幹嘛？快講。", "…What? Spit it out."));
  if (S.crisis) S.crisis.at += 2;
  addWait({
    id: "why",
    text: tr("阿凱停下來了：說出你的理由", "Kai has stopped: say why"),
    untilT: S.t + 2,
    urgent: true,
    icon: tr("！", "!"),
    clearOn: "manual",
  });
  focusInput();
}

function stopped(how: "precise" | "escalate", line?: string): void {
  if (S.stopBy) return;
  S.stopBy = how;
  S.stopAt = S.t;
  if (line) S.stopLines.push(line);
  for (const id of ["clipping", "cutting", "why"]) clearWait(id);
  if (how === "precise") {
    S.trust += 2;
    eventBanner(tr("暫停", "Pause"), tr("阿凱停手", "Kai stops"));
    post(
      "table",
      "npc",
      "gs_r",
      tr(
        "……好啦。你說的對，我看不到兩條。佩佩，打給主任，說 Calot 看不清楚，請他回來看。",
        "…Fine. You’re right, I can’t see two structures. Pei-Pei, call Dr. Chiang. Tell him Calot’s isn’t clear and ask him to come back and look.",
      ),
    );
    post("table", "npc", "circ", tr("好，我打給 6 房。", "Okay, calling OR 6."));
    S.vsDue = Math.min(S.vsDue ?? S.t + 3, S.t + 3);
  }
  S.cmd = null;
  clearWait("cam");
  S.step = "rcalot";
}

function vsReturns(): void {
  S.vsAway = false;
  S.backAt = S.t;
  S.vsDue = null;
  const r = ent("gs_r");
  walkEnt(r, R_SPOT);
  r.onArrive = () => {
    r.face = "right";
  };
  const v = ent("gs_vs");
  v.hidden = false;
  [v.x, v.y] = DOOR;
  v.fx = v.x;
  v.fy = v.y;
  rebuildDyn();
  walkEnt(v, VS_SPOT);
  v.onArrive = () => {
    v.face = "left";
    v.working = true;
  };
  eventBanner(tr("江主任回來了", "Dr. Chiang is back"), tr("重新刷手、換手套", "Rescrubbed, fresh gloves"));
  if (S.recognized) {
    post(
      "table",
      "npc",
      "gs_vs",
      tr("給我看。……斷了。這是總膽管。", "Show me. …It’s cut. That’s the common bile duct."),
    );
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "轉開腹。佩佩，打給家屬說手術會延長，叫 6 房的學長來幫忙。",
        "Converting to open. Pei-Pei, call the family and tell them the surgery will run longer, and get one of the seniors from OR 6 to come help.",
      ),
    );
    convertOpen();
    return;
  }
  if (S.cbdCut) {
    S.crisis = null;
    post("table", "npc", "gs_vs", tr("怎麼樣？", "How’s it going?"));
    post(
      "table",
      "npc",
      "gs_r",
      tr(
        "Cystic duct 跟 artery 都夾斷了，在剝肝床。",
        "Cystic duct and artery are both clipped and cut. I’m on the liver bed.",
      ),
    );
    post("table", "npc", "gs_vs", tr("這麼快？……好，我來。", "That fast? …Okay, I’ll take it."));
    runSeq([
      [0, "bed"],
      [4, "bag"],
      [6, "extract"],
      [7, "count"],
      [8, "close"],
    ]);
    return;
  }
  S.crisis = null;
  S.path = "safe";
  if (!S.stopBy && S.calledVs !== null && !S.clipCBD) S.stopBy = "escalate";
  for (const id of ["clipping", "cutting", "why"]) clearWait(id);
  if (S.clipCBD) {
    S.clipRemoved = true;
    post(
      "table",
      "npc",
      "gs_vs",
      tr("等一下。這個 clip 夾在哪裡？……鏡頭給我靠近。", "Wait. Where’s this clip? …Bring the camera in."),
    );
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "拿掉。這條是總膽管。九毫米，cystic duct 哪有這麼粗。",
        "Take it off. This is the common bile duct. Nine millimeters. No cystic duct is that thick.",
      ),
    );
    post(
      "table",
      "narr",
      null,
      tr(
        "主任小心地把 clip 拆下來。管壁有一道壓痕，但沒有破，也沒有膽汁滲出。",
        "Dr. Chiang carefully takes the clip off. There’s a crush mark on the duct wall, but it isn’t torn and no bile is leaking.",
      ),
    );
    finding(
      tr("術中", "Intraop"),
      tr(
        "總膽管被夾一個 clip，主任回來後拆除；管壁完整，無膽汁滲漏。",
        "A clip was placed on the CBD and removed after the attending returned; duct wall intact, no bile leak.",
      ),
    );
  } else if (S.stopBy === "precise") {
    post(
      "table",
      "npc",
      "gs_r",
      tr(
        "主任，學弟說 CVS 還沒有，我也看不到兩條。",
        "Dr. Chiang, the student said we don’t have the CVS yet, and I can’t see two structures either.",
      ),
    );
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "停得好。……你看，這條是總膽管，被拉起來了。這種發炎的 Calot，最常出事。",
        "Good stop. …Look, this is the common bile duct, tented up. An inflamed Calot’s like this is where things go wrong most.",
      ),
    );
  } else {
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "我看一下。……停。CVS 呢？三角裡都是脂肪，你看到幾條？這條是總膽管。",
        "Let me look. …Stop. Where’s your CVS? The triangle is all fat. How many structures do you see? This is the common bile duct.",
      ),
    );
    post("table", "npc", "gs_r", tr("……抱歉。", "…Sorry."));
  }
  runSeq([
    [0, "inspect"],
    [1, "bailQ"],
    [3, "ioc"],
    [6, "iocAsk"],
    [10, "open"],
    [12, "stones"],
    [13, "hunt"],
  ]);
}

/** The clerk's words made someone realise the duct was cut. */
function recognize(by: string): void {
  if (S.recognized || !S.cbdCut) return;
  S.recognized = true;
  S.recognizedAt = S.t;
  S.recognizedBy = by;
  S.path = "cutKnown";
  S.crisis = null;
  for (const id of ["clipping", "cutting", "why"]) clearWait(id);
  eventBanner(tr("總膽管損傷", "CBD injury"), tr("術中發現", "Recognized intraop"), true);
  if (S.vsAway) {
    post("table", "npc", "gs_r", tr("……你是說……這是總膽管？", "…You mean… this is the common bile duct?"));
    post(
      "table",
      "narr",
      null,
      tr(
        "阿凱的手停在半空中。他看了很久，臉色慢慢變白。",
        "Kai’s hands freeze in midair. He stares for a long time, and the color slowly drains from his face.",
      ),
    );
    post(
      "table",
      "npc",
      "gs_r",
      tr(
        "佩佩，馬上叫主任回來。說……總膽管可能斷了。",
        "Pei-Pei, get Dr. Chiang back right now. Tell him… the common bile duct might be cut.",
      ),
    );
    S.vsDue = Math.min(S.vsDue ?? S.t + 2, S.t + 2);
    return;
  }
  post("table", "npc", "gs_vs", tr("……停。鏡頭給我。", "…Stop. Give me the camera."));
  post(
    "table",
    "narr",
    null,
    tr(
      "主任把鏡頭對準那兩個 clip，又往下找。斷端下面，還有一截粗管子，正在滲膽汁。",
      "Dr. Chiang aims the camera at the two clips, then searches lower. Below the cut end there’s another length of thick duct, leaking bile.",
    ),
  );
  post(
    "table",
    "npc",
    "gs_vs",
    tr(
      "這是總膽管。斷了。轉開腹。佩佩，打給家屬，說手術會延長；叫 6 房的學長來幫忙。",
      "This is the common bile duct. It’s cut. Converting to open. Pei-Pei, call the family and tell them the surgery will run longer; get one of the seniors from OR 6 to come help.",
    ),
  );
  convertOpen();
}

function convertOpen(): void {
  S.seq = [];
  S.hunt.on = false;
  clearWait("findst");
  setStep("convert");
  S.atCamera = false;
  S.cmd = null;
  clearWait("cam");
  runSeq([
    [2, "close"],
    [3, "post"],
  ]);
}

function startHunt(): void {
  S.hunt.on = true;
  S.hunt.until = S.t + 8;
  if (S.atCamera) {
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "有幾顆滾開了。你用鏡頭找，看到就點給我。低的地方先看。",
        "A few rolled away. Find them with the camera and click each one you see. Check the low spots first.",
      ),
    );
    addWait({
      id: "findst",
      text: tr("用鏡頭找滾開的結石，看到就點它", "Find the spilled stones with the camera; click each one you see"),
      clearOn: "manual",
      icon: "◎",
    });
    scope.open();
  } else
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "有幾顆滾開了。阿凱，鏡頭給我，我自己找。",
        "A few rolled away. Kai, give me the camera, I’ll find them myself.",
      ),
    );
}

function endHunt(by: "clerk" | "timeout"): void {
  if (!S.hunt.on) return;
  S.hunt.on = false;
  clearWait("findst");
  if (by === "timeout") {
    S.hunt.byR = S.stones.length;
    const left = S.stones.length > 1 ? 1 : 0;
    const where = S.stones[0]?.where ?? "";
    S.stones = S.stones.slice(0, left);
    post(
      "table",
      "npc",
      "gs_vs",
      left
        ? tr(
            `時間不夠了，我找到剩下的……${where}那顆找不到了。記在手術紀錄上，術後追蹤有沒有膿瘍。`,
            `We’re out of time. I found the rest… the one ${where}, I can’t find. Put it in the op note, and follow up for an abscess after surgery.`,
          )
        : tr("好，剩下的我找到了。", "Okay, I found the rest."),
    );
    if (left)
      finding(
        tr("術中", "Intraop"),
        tr(`散落的結石有一顆沒找到（${where}）。`, `One spilled stone was not found (${where}).`),
      );
  } else
    post(
      "table",
      "npc",
      "gs_vs",
      tr(
        "全部了。數一下：片子上多顆，袋子裡我數到十二顆。好。",
        "That’s all of them. Count: multiple on the films, and I count twelve in the bag. Good.",
      ),
    );
  closeZoom();
  runSeq([
    [1, "suture"],
    [3, "cautery"],
    [5, "drain"],
    [6, "bag"],
    [8, "extract"],
    [9, "count"],
    [10, "close"],
  ]);
}

/* ---------- table dialog ---------- */

const TIMEOUT: TimeoutItem[] = [
  {
    k: tr("術式", "Procedure"),
    opts: [
      {
        v: tr("腹腔鏡膽囊切除（必要時轉開腹或部分切除）", "Lap chole (convert to open or subtotal if needed)"),
        ok: true,
        reply: tr(
          "腹腔鏡膽囊切除，確認。同意書有寫轉開腹和部分切除。",
          "Lap chole, confirmed. The consent covers conversion to open and subtotal.",
        ),
      },
      {
        v: tr("腹腔鏡膽囊切除＋總膽管探查", "Lap chole + CBD exploration"),
        ok: false,
        reply: tr(
          "ERCP 已經清乾淨了，誰說要探查？看病歷。",
          "ERCP already cleared the duct. Who said anything about exploring it? Read the chart.",
        ),
      },
      {
        v: tr("不知道", "Not sure"),
        ok: false,
        reply: tr("你要上台的刀，你不知道？", "You’re scrubbing into this case and you don’t know?"),
      },
    ],
  },
  {
    k: tr("過敏", "Allergies"),
    opts: [
      {
        v: tr("無藥物過敏（病歷與手圈都確認）", "No drug allergies (chart and wristband checked)"),
        ok: true,
        reply: tr("無藥物過敏，確認。", "No drug allergies, confirmed."),
      },
      {
        v: tr("Penicillin（起疹子）", "Penicillin (rash)"),
        ok: false,
        reply: tr(
          "誰說的？病歷第一頁寫無藥物過敏。不要用猜的。",
          "Says who? Page one of the chart says no drug allergies. Don’t guess.",
        ),
      },
      {
        v: tr("顯影劑過敏", "Contrast allergy"),
        ok: false,
        reply: tr(
          "他 ERCP 打了一堆顯影劑都沒事。病歷寫無藥物過敏。",
          "He got a load of contrast for his ERCP with no problem. The chart says no drug allergies.",
        ),
      },
    ],
  },
  {
    k: tr("抗生素", "Antibiotics"),
    opts: [
      {
        v: tr("病房 08:00 已給 Ceftriaxone 2 g（治療中）", "Ceftriaxone 2 g given on the ward at 08:00 (treatment)"),
        ok: true,
        reply: tr(
          "對，治療劑量還在打，不用另外給預防性的。",
          "Right. He’s on a treatment dose; no need for separate prophylaxis.",
        ),
      },
      {
        v: tr("還沒給，現在給 Cefazolin 1 g", "Not given yet; give cefazolin 1 g now"),
        ok: false,
        reply: tr(
          "他本來就在打膽管炎的 Ceftriaxone，治療劑量還在，不用另外給預防性的。看病歷。",
          "He’s already on ceftriaxone for his cholangitis, at a treatment dose; no need for separate prophylaxis. Read the chart.",
        ),
      },
      {
        v: tr("不知道", "Not sure"),
        ok: false,
        reply: tr(
          "佩佩，念給他聽。早上八點病房給過了。",
          "Pei-Pei, read it to him. The ward gave it at eight this morning.",
        ),
      },
    ],
  },
  {
    k: tr("影像", "Imaging"),
    opts: [
      {
        v: tr("MRCP 與 ERCP 已上螢幕；總膽管已清乾淨", "MRCP and ERCP on screen; CBD cleared"),
        ok: true,
        reply: tr("片子上了，確認。", "Films are up, confirmed."),
      },
      {
        v: tr("影像還沒調出來", "Images not pulled up yet"),
        ok: false,
        reply: tr("佩佩一早就調好了，你沒看。", "Pei-Pei had them up first thing. You didn’t look."),
      },
      {
        v: tr("只有腹部超音波", "Abdominal ultrasound only"),
        ok: false,
        reply: tr("他做過 MRCP 跟 ERCP。去看 PACS。", "He’s had MRCP and ERCP. Go look at the PACS."),
      },
    ],
  },
];

const PHASE_SUB: Record<Phase, string> = {
  preop: tr("黃先生還醒著", "Mr. Huang is still awake"),
  timeout: "Time-out",
  lap: tr("腹腔鏡中", "Laparoscopy"),
  close: tr("關閉中", "Closing"),
  post: tr("手術結束", "Case over"),
};

function endTimeout(): void {
  if (S.timeout.done) return;
  S.timeout.done = true;
  clearWait("to");
  S.draped = true;
  S.phase = "lap";
  S.lapAt = S.t;
  eventBanner(tr("劃刀", "Incision"), tr("腹腔鏡膽囊切除", "Lap cholecystectomy"));
  setStep("ports");
  for (const id of ["gs_vs", "gs_r"]) {
    const e = findEnt(id);
    if (e) e.working = true;
  }
  post(
    "table",
    "narr",
    null,
    S.atCamera
      ? tr("鏡頭放進去了。", "The scope is in.")
      : S.sterile
        ? tr("鏡頭放進去了。阿凱先扶著，你可以接過來。", "The scope is in. Kai holds it for now; you can take it over.")
        : tr("鏡頭放進去了。阿凱先扶著。", "The scope is in. Kai holds it for now."),
  );
  runSeq([
    [2, "explore"],
    [4, "fundus"],
    [6, "adhesio"],
    [9, "hartmann"],
    [11, "peri"],
    [13, "calot"],
  ]);
}

function tableChoices(): ChoiceItem[] {
  if (S.phase === "timeout" && S.timeout.withClerk)
    return timeoutChoices(TIMEOUT, S.timeout.ans, {
      ch: "table",
      okWho: "circ",
      badWho: "gs_vs",
      onWrong: () => {
        S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
      },
      onDone: endTimeout,
    });
  if (S.phase === "lap") {
    const out: ChoiceItem[] = [];
    if (S.sterile && S.step !== "convert") {
      if (!S.atCamera)
        out.push(
          C(tr("接過鏡頭", "Take the camera"), () => {
            S.atCamera = true;
            clearWait("cam0");
            flag("gs_vs");
            me("table", tr("（接過鏡頭）", "(Takes the camera)"));
            post(
              "table",
              "npc",
              "gs_r",
              tr(
                "給你。穩一點，病人的右邊在畫面左邊。",
                "Here. Keep it steady. The patient’s right is on the left of the screen.",
              ),
            );
            scope.open();
          }),
        );
      else out.push(C(tr("看鏡頭畫面", "View the scope"), scope.open));
    }
    if (S.vsAway && !S.stopBy && !S.cbdCut && !S.holdUsed) out.push(C(tr("喊停", "Stop!"), holdOn, false, "warn"));
    if (S.ioc.shown) out.push(C(tr("看 C-arm 螢幕", "View C-arm screen"), openFluoro));
    return out;
  }
  if (S.phase === "close" && S.offerSuture && !S.sutured && S.sterile && S.path !== "cutKnown") {
    return [
      tr("關傷口", "Closing"),
      C(tr("縫皮（3 分鐘）", "Close skin (3 min)"), () => {
        S.sutured = true;
        clearWait("sut");
        flag("gs_vs");
        me("table", tr("（縫皮）", "(Closes the skin)"));
        post(
          "table",
          "narr",
          null,
          tr(
            "你在主任盯著下，縫好四個 trocar 傷口的皮。肚臍那個比較大，筋膜阿凱先關好了。",
            "Under Dr. Chiang’s eye, you close the skin over the four trocar sites. The umbilical one is bigger; Kai has already closed its fascia.",
          ),
        );
        post(
          "table",
          "npc",
          "gs_vs",
          S.cam.miss > S.cam.hit
            ? tr(
                "針距還可以。鏡頭要是也扶這麼穩就好了。",
                "Spacing’s acceptable. Wish you held the camera that steady.",
              )
            : tr("針距還可以。", "Spacing’s acceptable."),
        );
        spend(3);
      }),
    ];
  }
  return [];
}

async function tableTalk(text: string): Promise<void> {
  if (!S.asleep) {
    await chat(text, {
      ch: "table",
      think: "huang",
      speakers: tr(
        { huang: "黃先生", gs_vs: "江主任", gs_r: "阿凱", anes: "陳醫師（麻醉科）" },
        { huang: "Mr. Huang", gs_vs: "Dr. Chiang", gs_r: "Kai", anes: "Dr. Chen (anesthesia)" },
      ),
      cost: 1,
      body: tr(
        `你在醫學教育遊戲中扮演手術台上還醒著、馬上要麻醉的「黃先生」（55 歲計程車司機），江主任、阿凱、麻醉科陳醫師偶爾插一句。玩家是一般外科見習醫學生（clerk）。
背景：三天前在外科門診被診斷急性膽管炎、收住院，做了 ERCP 取出總膽管結石，今天來做腹腔鏡膽囊切除。黃先生講話直、帶點台語腔（「歹勢」「啊」），表面上豪爽，其實很緊張。
他會說的事（clerk 問到或安撫他時才說），括號是 id：
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
如果 clerk 清楚說明「可能轉開腹、可能只切一部分、可能放引流管」，explained 設為 true，黃先生會說「喔……那也是為了安全齁」。
江主任：只在 clerk 講錯醫學內容時簡短修正。陳醫師：只在談到麻醉、禁食時插話。不要講太多醫學細節。`,
        `In a medical education game you are playing "Mr. Huang" (55, a taxi driver), lying on the operating table, still awake and about to be put under; Dr. Chiang, Kai, and Dr. Chen from anesthesia chime in now and then. The player is a general surgery clerk (medical student).
Background: three days ago he was diagnosed with acute cholangitis in the surgery clinic and admitted; ERCP removed his common bile duct stones, and today he is here for a laparoscopic cholecystectomy. Mr. Huang is blunt and talks in plain, folksy English, with the odd sheepish "sorry, sorry" and a drawn-out "ah"; he acts hearty on the surface but is actually very nervous.
Things he will say (only when the clerk asks or reassures him); the id is in parentheses:
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
If the clerk clearly explains that "it might convert to open, might remove only part of it, might leave a drain", set explained to true, and Mr. Huang says something like "Oh... well, that's for safety, huh."
Dr. Chiang: only briefly corrects the clerk when they get medical content wrong. Dr. Chen: only chimes in about anesthesia or NPO. Don't go into too much medical detail.`,
      ),
      extra: `"revealed":[],"explained":false`,
      after(r) {
        for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
        if (r.explained === true) S.explained = true;
      },
    });
    return;
  }
  const q = S.q;
  const crisisOn = S.vsAway && !S.stopBy && !S.cbdCut && S.crisis !== null;
  const cutHidden = S.cbdCut && !S.recognized;
  const stageTxt =
    S.crisis?.stage === "claim"
      ? tr("阿凱正在剝那條管子", "Kai is dissecting out that duct")
      : S.crisis?.stage === "ready"
        ? tr("阿凱說這條就是 cystic duct，正在要 clip", "Kai says this is the cystic duct and is calling for a clip")
        : S.crisis?.stage === "clip1"
          ? tr("clip applier 已經對準那條管子", "The clip applier is already lined up on that duct")
          : S.crisis?.stage === "clip2" || S.crisis?.stage === "cut"
            ? tr(
                "那條管子已經夾了一個 clip，阿凱準備再夾一個然後剪斷",
                "One clip is already on that duct; Kai is about to place another and then cut",
              )
            : "";
  await chat(text, {
    ch: "table",
    think: lead(),
    tier: "default",
    speakers: {
      ...(S.vsAway ? {} : { gs_vs: tr("江主任（主刀）", "Dr. Chiang (operating surgeon)") }),
      gs_r: S.vsAway
        ? tr("阿凱（R3，主任不在，暫時主刀）", "Kai (R3, operating while the attending is out)")
        : tr("阿凱（R3，第一助手）", "Kai (R3, first assist)"),
      anes: tr("陳醫師（麻醉科）", "Dr. Chen (anesthesia)"),
      scrub: tr("阿珍（刷手護理師）", "Jen (scrub nurse)"),
      circ: tr("佩佩（流動護理師）", "Pei-Pei (circulating nurse)"),
    },
    cost: 1,
    body: tr(
      `你在醫學教育遊戲中同時扮演手術室裡的人。正在做腹腔鏡膽囊切除：黃先生 55 歲，三天前急性膽管炎、ERCP 取石，膽囊發炎、肝膽三角（Calot）纖維化。MRCP：總膽管 9 mm，cystic duct 短、低位、和總膽管平行一段。玩家是見習醫學生（clerk）${S.atCamera ? "，正在扶鏡頭" : S.sterile ? "，穿著手術衣站在台邊" : "，沒有刷手，站在台下"}。
【每個人】
江主任：${S.vsAway ? "不在房間，被叫去 6 房處理出血。" : "肝膽胰外科主治，話少，開刀時會考學生，不直接說答案。"}
阿凱：R3，很累、很趕，技術不差但會用經驗代替確認。${S.vsAway ? "主任不在，他想趕快把這台做完。" : ""}
陳醫師：只管麻醉與血行動力學。阿珍：只管器械、計數與無菌；她心裡覺得不太對，但不會越級。佩佩：拿東西、打電話、記錄；clerk 請她打電話她會照做。
${
  crisisOn
    ? `【關鍵情境】${stageTxt}。真相：那條其實是被拉起來的總膽管；Critical view of safety 沒有達成（三角裡還都是脂肪、膽囊下段沒有離開肝床、只看得到一條構造進入膽囊）。
用 stop 判斷 clerk 這句話：
- precise：clerk 明確要求先不要夾／停下來，而且講出具體理由：CVS 沒有達成（至少提到一項：三角沒清乾淨、膽囊下段沒離開肝床、沒有看到兩條而且只有兩條構造進膽囊），或懷疑這條是總膽管（例如太粗、往下延伸到十二指腸、MRCP 說 9 mm）並建議等主任、做膽道攝影。阿凱會停手。
- vague：表達擔心但沒有具體理由（例如「這樣對嗎？」「要不要等主任？」）。阿凱會不耐煩地帶過（「我開過幾十台了」「主任說繼續」）。
- none：跟這件事無關。
阿凱只能用他自己的語氣回應，不要替 clerk 補理由。`
    : cutHidden
      ? `【關鍵情境】剛剛${S.vsAway ? "阿凱" : "阿凱（主任不在時）"}夾斷了一條他以為是 cystic duct 的管子，斷面滲出一點膽汁；${S.vsAway ? "主任還沒回來" : "主任回來後聽阿凱說都夾好了，正在收尾"}。真相：斷的是總膽管，沒有人發現。
用 stop 判斷 clerk 這句話：precise：clerk 明確指出剛剛剪斷的可能是總膽管（或看到膽汁漏出、管子太粗、要做膽道攝影確認），並要求停下來確認。vague：只是隱約擔心。none：無關。${S.vsAway ? "阿凱回應" : "江主任回應"}。`
      : `目前沒有需要判斷的事，stop 用 none。`
}
${q ? `【待回答的問題】江主任剛剛問 clerk：「${q}」參考答案：${QA[q] ?? ""}。用 judge 評判 clerk 這句話對這題的回答：correct、partial、wrong；如果這句話不是在回答這題，judge 用 none。只由江主任回應評判：答對補一句延伸，答錯直接說正確答案並唸一句。` : "沒有待回答的問題，judge 用 none。"}
回應要簡短，符合手術現場的節奏。`,
      `In a medical education game you are playing everyone in the operating room at once. A laparoscopic cholecystectomy is under way: Mr. Huang, 55, had acute cholangitis three days ago with ERCP stone extraction; the gallbladder is inflamed and the hepatocystic triangle (Calot's) is fibrotic. MRCP: CBD 9 mm; the cystic duct is short, low, and runs parallel to the CBD for a stretch. The player is a clerk (medical student)${S.atCamera ? ", currently holding the camera" : S.sterile ? ", gowned and standing at the table" : ", not scrubbed, standing away from the table"}.
[WHO'S WHO]
Dr. Chiang: ${S.vsAway ? "Not in the room; called to OR 6 to deal with bleeding." : "Hepatobiliary-pancreatic surgery attending; a man of few words who quizzes the student while operating and doesn't give away answers."}
Kai: R3, exhausted and in a hurry; technically decent, but substitutes experience for confirmation. ${S.vsAway ? "With the attending gone, he wants to get this case done fast." : ""}
Dr. Chen: only anesthesia and hemodynamics. Jen: only instruments, counts, and sterility; she feels something is off, but she won't go over anyone's head. Pei-Pei: fetches things, makes calls, keeps the record; if the clerk asks her to make a call, she does it.
${
  crisisOn
    ? `[CRITICAL SITUATION] ${stageTxt}. Truth: that "duct" is actually the common bile duct, tented up by traction; the critical view of safety has NOT been achieved (the triangle is still full of fat, the lower gallbladder hasn't come off the liver bed, only one structure can be seen entering the gallbladder).
Use stop to classify the clerk's line:
- precise: the clerk explicitly asks not to clip yet / to stop, AND gives a concrete reason: the CVS isn't achieved (mentions at least one of: triangle not cleared, lower gallbladder not off the liver bed, not seeing two and only two structures entering the gallbladder), or suspects this is the common bile duct (e.g. too thick, runs down to the duodenum, MRCP said 9 mm) and suggests waiting for the attending or doing a cholangiogram. Kai stops.
- vague: expresses worry without a concrete reason (e.g. "Is this right?" "Should we wait for the attending?"). Kai brushes it off impatiently ("I've done dozens of these", "The attending said keep going").
- none: unrelated to this.
Kai responds only in his own voice; do not supply reasons on the clerk's behalf.`
    : cutHidden
      ? `[CRITICAL SITUATION] ${S.vsAway ? "Kai" : "Kai (while the attending was out)"} just clipped and cut a duct he believed was the cystic duct, and a little bile is oozing from the cut end; ${S.vsAway ? "the attending isn't back yet" : "the attending came back, heard from Kai that everything is clipped, and is finishing up"}. Truth: what was cut is the common bile duct, and nobody has noticed.
Use stop to classify the clerk's line: precise: the clerk explicitly points out that what was just cut may be the common bile duct (or notes bile leaking, the duct being too thick, wants a cholangiogram to confirm) and asks to stop and check. vague: only a vague worry. none: unrelated. ${S.vsAway ? "Kai responds" : "Dr. Chiang responds"}.`
      : `Nothing to classify right now; set stop to none.`
}
${q ? `[PENDING QUESTION] Dr. Chiang just asked the clerk: "${q}" Reference answer: ${QA[q] ?? ""}. Use judge to grade the clerk's line as an answer to this question: correct, partial, wrong; if the line isn't answering this question, set judge to none. Only Dr. Chiang responds to the grading: if right, he adds one line taking it further; if wrong, he states the correct answer and gives a short reprimand.` : "No pending question; set judge to none."}
Keep replies short, at the pace of a real operating room.`,
    ),
    extra: `"judge":"none","stop":"none"`,
    after(r) {
      if (q && isGrade(r.judge) && S.q === q) {
        S.qRes.push({ q, j: r.judge });
        S.q = null;
        clearWait("q");
        if (r.judge === "wrong") S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
        judged(r.judge);
      }
      const stop = isStop(r.stop) ? r.stop : "none";
      if (crisisOn && stop !== "none") {
        S.stopLines.push(text);
        if (stop === "precise") stopped("precise", text);
        else {
          S.vagueN++;
          clearWait("why");
          if (S.vagueN === 2 && S.crisis) {
            S.crisis.at += 1;
            post("table", "npc", "gs_r", tr("……好啦，我再看一下。", "…Fine, I’ll take another look."));
          }
        }
      } else if (cutHidden && stop === "precise") recognize("clerk");
      else if (cutHidden && stop === "vague") S.stopLines.push(text);
    },
  });
}

/* ---------- close-ups ---------- */

function drawFluoro(c: Ctx, now: number): void {
  R(c, "#1a1a1e", 0, 0, 192, 108);
  dither(c, 0, 0, 192, 108, "#2a2a30", 0.5);
  // Spine and ribs.
  R(c, "#4a4a52", 136, 0, 22, 108);
  for (let y = 4; y < 108; y += 14) R(c, "#5a5a62", 138, y, 18, 8);
  for (let i = 0; i < 5; i++) pline(c, 0, 10 + i * 16, 136, 2 + i * 18, "#38383e", 3);
  const ink = "#0a0a0c";
  // Gallbladder (injected), short cystic duct, CBD down to the duodenum, CHD and intrahepatic tree up.
  pell(c, 66, 40, 15, 10, "#1a1a20");
  pell(c, 66, 40, 13, 8, ink);
  for (const [x, y] of [
    [60, 38],
    [70, 42],
    [64, 44],
  ] as Cell[])
    pell(c, x, y, 2, 2, "#3a3a40");
  pline(c, 76, 48, 90, 58, ink, 3);
  pline(c, 96, 22, 95, 86, ink, 5);
  pline(c, 96, 22, 84, 8, ink, 3);
  pline(c, 96, 22, 112, 6, ink, 3);
  pline(c, 88, 14, 76, 10, ink, 2);
  pline(c, 106, 12, 118, 16, ink, 2);
  pell(c, 118, 92, 22, 7, "#141418");
  dither(c, 96, 86, 46, 12, ink, 0.5);
  // The clip that was taken off still casts a ghost on the earlier shot — not here; instruments only.
  pline(c, 160, 0, 120, 44, "#7a7a82", 2);
  ptext(c, "IOC", 4, 4, "#d9dcea");
  if (Math.floor(now / 600) % 2 === 0) R(c, "#d9dcea", 4, 100, 3, 3);
}

/** The same parts on the real cholangiogram (image pixels; contrast goes in through the catheter). */
const FLUORO_PHOTO: Record<FluoroPart, { x: number; y: number; w: number; h: number; label: string }> = {
  gb: {
    x: 70,
    y: 52,
    w: 40,
    h: 24,
    label: tr("導管（從膽囊那側打顯影劑）", "Catheter (contrast goes in from the gallbladder side)"),
  },
  cystic: { x: 43, y: 36, w: 9, h: 14, label: tr("導管尖端接著的一小段", "The short bit at the catheter tip") },
  cbd: { x: 49, y: 50, w: 10, h: 38, label: tr("彎彎的那條往下走", "The curving one running down") },
  chd: { x: 40, y: 8, w: 28, h: 27, label: tr("往上分岔的那幾條", "The branches going up") },
  duod: { x: 14, y: 56, w: 28, h: 44, label: tr("左下一節一節的那團", "The segmented mass at lower left") },
};

function fluoroZoom(): ZoomCfg {
  const ask = S.ioc.ask;
  const parts = (Object.keys(FLUORO_PARTS) as FluoroPart[]).filter((k) => k !== "gb");
  return {
    title: tr("術中膽道攝影", "Intraoperative cholangiogram"),
    sub: ask
      ? tr(`江主任：指給我看「${FLUORO_NAME[ask]}」`, `Dr. Chiang: show me the ${FLUORO_NAME[ask]}`)
      : tr("C-arm 螢幕", "C-arm screen"),
    draw: drawFluoro,
    hot: () =>
      (Object.keys(FLUORO_PARTS) as FluoroPart[]).map((k) => ({
        ...FLUORO_PARTS[k],
        fn: () => fluoroPick(k),
      })),
    photo: {
      id: "ioc",
      overlay: (c, p) => sideMark(c, p),
      // The catheter is listed last so the ducts it crosses win the click.
      hot: () =>
        [...parts, "gb" as const].map((k) => ({
          ...FLUORO_PHOTO[k],
          fn: () => fluoroPick(k),
        })),
    },
  };
}

function openFluoro(): void {
  closeZoom();
  openZoom(fluoroZoom());
}

function fluoroPick(k: FluoroPart): void {
  const want = S.ioc.ask;
  if (!want) {
    note(tr(`${FLUORO_NAME[k]}。`, `The ${FLUORO_NAME[k]}.`));
    return;
  }
  me("table", tr(`（指著螢幕：${FLUORO_PARTS[k].label}）`, `(Points at the screen: ${FLUORO_PARTS[k].label})`));
  S.ioc.picks.push(`${FLUORO_NAME[want]}→${FLUORO_NAME[k]}`);
  if (k === want) {
    S.trust++;
    trustCheck();
    if (want === "cystic") {
      post(
        "table",
        "npc",
        "gs_vs",
        tr(
          "對。很短，貼著總膽管走一段才進去。那剛剛阿凱要夾的那條呢？",
          "Right. Very short; it runs along the CBD for a stretch before it joins. So what about the one Kai was about to clip?",
        ),
      );
      S.ioc.ask = "cbd";
      openFluoro();
    } else {
      post(
        "table",
        "npc",
        "gs_vs",
        tr("對，總膽管。九毫米。記住這張片子。", "Right, the common bile duct. Nine millimeters. Remember this film."),
      );
      S.ioc.ask = null;
      clearWait("ioc");
      closeZoom();
    }
  } else {
    S.trust--;
    trustCheck();
    post("table", "npc", "gs_vs", tr(`那是${FLUORO_NAME[k]}。再看。`, `That’s the ${FLUORO_NAME[k]}. Look again.`));
  }
}

function drawMrcp(c: Ctx): void {
  R(c, "#08080c", 0, 0, 192, 108);
  dither(c, 0, 0, 192, 108, "#14141a", 0.4);
  const fl = "#e8eef8";
  pell(c, 70, 44, 20, 13, "#b8c0d0");
  pell(c, 70, 44, 18, 11, fl);
  for (const [x, y] of [
    [62, 40],
    [72, 46],
    [78, 40],
    [66, 50],
    [58, 46],
  ] as Cell[])
    pell(c, x, y, 3, 2, "#3a3a44");
  pline(c, 84, 54, 96, 66, fl, 2);
  pline(c, 100, 18, 99, 92, fl, 5);
  pline(c, 100, 18, 88, 4, fl, 3);
  pline(c, 100, 18, 116, 2, fl, 3);
  pline(c, 108, 92, 150, 86, "#9aa8c0", 2);
  pell(c, 122, 98, 24, 6, "#5a6478");
  ptext(c, "MRCP", 4, 4, "#d9dcea");
  ptext(c, "CBD 9mm", 120, 40, "#ffd447");
  pline(c, 118, 44, 103, 52, "#ffd447", 1);
}

function mrcpZoom(): ZoomCfg {
  return {
    title: tr("PACS：MRCP（入院時）", "PACS: MRCP (on admission)"),
    sub: tr("佩佩幫你把片子調出來", "Pei-Pei pulled up the images for you"),
    draw: drawMrcp,
    hot: [
      {
        x: 50,
        y: 30,
        w: 40,
        h: 28,
        label: tr("膽囊：多顆結石，壁增厚", "Gallbladder: multiple stones, thickened wall"),
        fn: () => undefined,
      },
      {
        x: 82,
        y: 52,
        w: 16,
        h: 16,
        label: tr(
          "膽囊管：很短，低位，和總膽管平行一段才匯入",
          "Cystic duct: very short and low, parallel to the CBD for a stretch before joining",
        ),
        fn: () => undefined,
      },
      {
        x: 94,
        y: 20,
        w: 12,
        h: 70,
        label: tr("總膽管：9 mm（ERCP 後已無結石）", "CBD: 9 mm (stone-free after ERCP)"),
        fn: () => undefined,
      },
      {
        x: 84,
        y: 0,
        w: 36,
        h: 18,
        label: tr("總肝管與左右肝管", "Common hepatic and right/left hepatic ducts"),
        fn: () => undefined,
      },
      { x: 100, y: 90, w: 50, h: 14, label: tr("十二指腸與胰管", "Duodenum and pancreatic duct"), fn: () => undefined },
    ],
    photo: {
      id: "mrcp",
      overlay: (c, p) => sideMark(c, p),
      hot: [
        {
          x: 5,
          y: 8,
          w: 30,
          h: 24,
          label: tr("膽囊：裡面好幾顆結石", "Gallbladder: several stones inside"),
          fn: () => undefined,
        },
        {
          x: 35,
          y: 24,
          w: 8,
          h: 18,
          label: tr(
            "膽囊管：彎彎曲曲，和總膽管平行一段才匯入",
            "Cystic duct: tortuous, parallel to the CBD for a stretch before joining",
          ),
          fn: () => undefined,
        },
        {
          x: 38,
          y: 66,
          w: 14,
          h: 14,
          label: tr(
            "總膽管遠端：好幾顆結石（入院時，ERCP 前）",
            "Distal CBD: several stones (on admission, before ERCP)",
          ),
          fn: () => undefined,
        },
        { x: 43, y: 32, w: 11, h: 34, label: tr("總膽管：擴張", "CBD: dilated"), fn: () => undefined },
        {
          x: 36,
          y: 0,
          w: 24,
          h: 18,
          label: tr("總肝管與左右肝管", "Common hepatic and right/left hepatic ducts"),
          fn: () => undefined,
        },
        { x: 56, y: 44, w: 40, h: 20, label: tr("胰管", "Pancreatic duct"), fn: () => undefined },
        { x: 26, y: 74, w: 12, h: 18, label: tr("十二指腸", "Duodenum"), fn: () => undefined },
      ],
    },
  };
}

/* ---------- monitor & knowledge ---------- */

function monData(): MonitorData {
  if (!S.asleep) return { hr: 88, sbp: 138, dbp: 84, spo2: 98, rr: 16 };
  if (S.step === "convert") return { hr: 96, sbp: 118, dbp: 70, spo2: 100, rr: 12, co2: 36 };
  if (S.phase === "lap") return { hr: 74, sbp: 124, dbp: 76, spo2: 99, rr: 12, co2: S.vsAway ? 42 : 38 };
  return { hr: 70, sbp: 120, dbp: 72, spo2: 100, rr: 12, co2: 36 };
}

const JUDGE_LABEL: Record<Judge, string> = {
  correct: tr("答對", "correct"),
  partial: tr("部分", "partial"),
  wrong: tr("答錯", "wrong"),
  none: tr("沒回答", "no answer"),
  skipped: tr("改問阿凱", "passed to Kai"),
};

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `刷手：${S.scrubbed ? "有" : "沒有"}；目前無菌：${S.sterile ? "是" : "否"}；污染次數：${S.contam}${S.banned ? "；被請下台" : ""}`,
      `Scrubbed: ${S.scrubbed ? "yes" : "no"}; currently sterile: ${S.sterile ? "yes" : "no"}; contaminations: ${S.contam}${S.banned ? "; sent off the table" : ""}`,
    ),
  );
  out.push(
    tr(
      `Time-out：${S.timeout.withClerk ? "在場" : "不在場"}；回答：${
        Object.entries(S.timeout.ans)
          .map(([k, v]) => k + "=" + v)
          .join("、") || "無"
      }`,
      `Time-out: ${S.timeout.withClerk ? "present" : "absent"}; answers: ${
        Object.entries(S.timeout.ans)
          .map(([k, v]) => k + "=" + v)
          .join(", ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `讀過病歷：${S.chartRead ? "是" : "否"}；看過 MRCP：${S.pacsRead ? "是（總膽管 9 mm、cystic duct 短而低位）" : "否"}`,
      `Read the chart: ${S.chartRead ? "yes" : "no"}; viewed MRCP: ${S.pacsRead ? "yes (CBD 9 mm, short low cystic duct)" : "no"}`,
    ),
  );
  out.push(
    tr(
      `術前和黃先生談過：${S.revealed.size ? [...S.revealed].map((id) => FACTS[id]).join("；") : "沒有"}；說明可能轉開腹／部分切除：${S.explained ? "有" : "沒有"}`,
      `Talked with Mr. Huang before surgery: ${S.revealed.size ? [...S.revealed].map((id) => FACTS[id]).join("; ") : "no"}; explained possible conversion / subtotal: ${S.explained ? "yes" : "no"}`,
    ),
  );
  out.push(
    tr(
      `扶鏡頭：正確 ${S.cam.hit}、看錯邊 ${S.cam.wrong}、漏掉 ${S.cam.miss}`,
      `Camera: correct ${S.cam.hit}, wrong side ${S.cam.wrong}, missed ${S.cam.miss}`,
    ),
  );
  out.push(
    tr(
      `主任提問與評判：${S.qRes.map((q) => q.q + "→" + q.j).join("；") || "無"}`,
      `Attending's questions and grades: ${S.qRes.map((q) => q.q + " → " + q.j).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `主任離開期間：在鏡頭上拉近看那條管子：${S.ductLook ? "有" : "沒有"}；按「喊停」：${S.holdUsed ? "有" : "沒有"}；clerk 說過的話：${S.stopLines.join("／") || "無"}；請佩佩找主任：${S.calledVs !== null ? clock(S.calledVs) : "沒有"}`,
      `While the attending was away: zoomed in on that duct with the camera: ${S.ductLook ? "yes" : "no"}; pressed "Stop!": ${S.holdUsed ? "yes" : "no"}; what the clerk said: ${S.stopLines.join(" / ") || "nothing"}; asked Pei-Pei to call the attending: ${S.calledVs !== null ? clock(S.calledVs) : "no"}`,
    ),
  );
  out.push(
    tr(
      `結果：${pathName()}${S.stopAt !== null ? `（${clock(S.stopAt)} 停手）` : ""}${S.recognizedAt !== null ? `（${clock(S.recognizedAt)} 發現）` : ""}`,
      `Outcome: ${pathName()}${S.stopAt !== null ? ` (stopped at ${clock(S.stopAt)})` : ""}${S.recognizedAt !== null ? ` (recognized at ${clock(S.recognizedAt)})` : ""}`,
    ),
  );
  out.push(
    tr(
      `術中膽道攝影指認：${S.ioc.picks.join("、") || "無"}`,
      `Cholangiogram identifications (asked → picked): ${S.ioc.picks.join(", ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `散落結石：clerk 用鏡頭找到 ${S.hunt.found} 顆；指認紀錄：${S.hunt.picks.join("、") || "無"}；遺留：${leftStones()} 顆`,
      `Spilled stones: clerk found ${S.hunt.found} with the camera; pick log: ${S.hunt.picks.join(", ") || "none"}; left behind: ${leftStones()}`,
    ),
  );
  out.push(
    tr(
      `術後與黃太太對話：${S.wifePost} 次；過度保證：${S.overpromise ? "是" : "否"}；把責任推給特定人：${S.blamed ? "是" : "否"}；隱瞞或說謊：${S.lied ? "是" : "否"}`,
      `Post-op talks with Mrs. Huang: ${S.wifePost}; overpromised: ${S.overpromise ? "yes" : "no"}; blamed a specific person: ${S.blamed ? "yes" : "no"}; concealed or lied: ${S.lied ? "yes" : "no"}`,
    ),
  );
  return out.join("\n");
}

/* ---------- endings ---------- */

function ending(): void {
  if (S.ending) return;
  let code: string;
  let name: string;
  if (S.path === "cutMissed") {
    code = "E";
    name = tr("沒有人發現", "Nobody Noticed");
  } else if (S.path === "cutKnown") {
    code = "D";
    name = tr("術中發現", "Caught on the Table");
  } else if (S.clipCBD) {
    code = "C";
    name = tr("千鈞一髮", "A Close Call");
  } else if (S.stopBy === "precise") {
    code = "A";
    name = "Critical View";
  } else {
    code = "B";
    name = tr("找主任回來", "Called the Attending Back");
  }
  const tail = S.banned
    ? tr("還有，明天去跟阿珍道歉。", "And tomorrow, go apologize to Jen.")
    : S.contam >= 2
      ? tr("無菌的東西，下次不要讓阿珍再講。", "Sterile technique: next time, don’t make Jen tell you again.")
      : !S.wifePost
        ? tr("家屬那邊我自己講了。下次叫你去，就要去。", "I talked to the family myself. Next time I send you, you go.")
        : S.overpromise
          ? tr("跟家屬講話不要打包票。", "Don’t make promises when you talk to families.")
          : "";
  const steps: EndingStep[] = [
    {
      fn: () => {
        S.draped = false;
      },
      who: "anes",
      text: tr("拔管了，呼吸很好。推恢復室。", "Extubated, breathing well. Off to recovery."),
    },
    {
      fn: () => {
        S.tableAnim = { t0: performance.now() };
        leaveBy(ent("anes"), DOOR);
        const alive = sessionGuard();
        setTimeout(() => {
          if (alive()) leaveBy(ent("circ"), DOOR);
        }, 600);
      },
      text: tr("黃先生被推往恢復室。", "Mr. Huang is wheeled off to recovery."),
      wait: 3000,
    },
  ];
  if (code === "E") {
    steps.push(
      {
        fn: () => walkEnt(ent("gs_vs"), [3, 7]),
        who: "gs_vs",
        text: tr("黃太太，手術很順利。", "Mrs. Huang, the surgery went very well."),
        wait: 2600,
      },
      {
        who: "huang_wife",
        text: tr(
          "謝謝主任！他說下禮拜就要回去開車了。",
          "Thank you, Doctor! He says he’s going back to driving next week.",
        ),
      },
      { text: tr("兩天後。", "Two days later.") },
      {
        text: tr(
          "黃先生的眼白又黃了。引流管流出金黃色的液體，一天三百多毫升。",
          "The whites of Mr. Huang’s eyes are yellow again. The drain is putting out golden fluid, over 300 mL a day.",
        ),
      },
      {
        text: tr(
          "MRCP：總膽管在肝門下方中斷，兩個 clip 夾在斷端上。",
          "MRCP: the common bile duct is cut off below the hilum, with two clips on the cut end.",
        ),
      },
      { who: "gs_vs", text: tr("那條管子……你在鏡頭上有看到嗎？", "That duct… did you see it on the camera?") },
    );
  } else if (code === "D") {
    steps.push(
      {
        text: tr(
          "轉開腹之後，江主任做了 Roux-en-Y 肝管空腸吻合。手術多了三個小時。",
          "After converting to open, Dr. Chiang performs a Roux-en-Y hepaticojejunostomy. The surgery runs three hours longer.",
        ),
      },
      {
        fn: () => walkEnt(ent("gs_vs"), [3, 7]),
        who: "gs_vs",
        text: tr(
          "黃太太，我要跟您說明一件事。手術中，膽管受傷了。",
          "Mrs. Huang, there’s something I need to explain to you. During the surgery, the bile duct was injured.",
        ),
        wait: 3200,
      },
      { who: "huang_wife", text: tr("……受傷？那他……會怎樣？", "…Injured? Then he… what’s going to happen to him?") },
      {
        who: "gs_vs",
        text: tr(
          "我們當場發現，已經接好了。接下來要住久一點，我會每天來看他。",
          "We caught it right away, and it’s been repaired. He’ll need to stay longer, and I’ll come see him every day.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "你剛剛那句話，讓我們在台上就發現。下一次，要在夾下去之前說。",
          "What you said in there is why we caught it on the table. Next time, say it before the clip goes on.",
        ),
      },
    );
  } else {
    const what =
      code === "C"
        ? tr(
            "總膽管被夾到一個 clip，我拆掉了，管壁看起來沒事。術後要注意黃疸和引流管的顏色。",
            "A clip got put on the common bile duct. I took it off, and the duct wall looks fine. After surgery we’ll watch for jaundice and the color of the drain output.",
          )
        : tr(
            "膽囊發炎很厲害，我們只切了一部分，有放一條引流管。",
            "The gallbladder was badly inflamed, so we removed only part of it, and we placed a drain.",
          );
    steps.push(
      {
        fn: () => walkEnt(ent("gs_vs"), [3, 7]),
        who: "gs_vs",
        text: tr(`黃太太，我是江主任。${what}`, `Mrs. Huang, I’m Dr. Chiang. ${what}`),
        wait: 3200,
      },
      {
        who: "huang_wife",
        text: S.wifePost
          ? tr("剛剛那個學生跟我講過了。謝謝你們。", "The student already told me. Thank you all.")
          : tr("那……他下禮拜可以開車嗎？", "So… can he drive next week?"),
      },
      {
        who: "gs_vs",
        text:
          code === "A"
            ? tr(
                "你今天那一句「停」，比你縫十條皮都重要。",
                "That one word of yours today, “stop,” matters more than ten skin closures.",
              )
            : code === "B"
              ? tr(
                  "找人回來是對的。下次，自己也要講得出為什麼。",
                  "Calling someone back was right. Next time, be able to say why yourself, too.",
                )
              : tr(
                  "Clip 夾下去之前，那一分鐘，你在想什麼？",
                  "That minute before the clip went on: what were you thinking?",
                ),
      },
    );
  }
  if (tail) steps.push({ who: "gs_vs", text: tail });
  void playEnding(code, name, steps, () => {
    const st = leftStones() ? tr(`；遺留結石 ${leftStones()} 顆`, `; stones left behind: ${leftStones()}`) : "";
    return tr(
      `${pathName()}。污染 ${S.contam} 次，鏡頭 對 ${S.cam.hit}／錯 ${S.cam.wrong}／漏 ${S.cam.miss}${st}。`,
      `${pathName()}. Contaminations: ${S.contam}; camera: ${S.cam.hit} right / ${S.cam.wrong} wrong side / ${S.cam.miss} missed${st}.`,
    );
  });
}

/* ---------- dialogs ---------- */

const dialogs: Level["dialogs"] = {
  chart: {
    title: tr("黃先生的病歷", "Mr. Huang’s chart"),
    sub: tr("護理紀錄電腦", "Nursing workstation"),
    ch: "chart",
    doc: doc(() => {
      S.chartRead = true;
      return [
        [tr("基本資料", "Patient"), tr("黃○○，55 歲，男性，計程車司機。", "Mr. Huang, 55, male, taxi driver.")],
        [
          tr("這次住院", "This admission"),
          tr(
            "三天前外科門診：急性膽管炎（Tokyo Guidelines Grade II），收住院、抗生素。住院後 ERCP：括約肌切開，取出總膽管結石。今天住院第三天，排腹腔鏡膽囊切除。",
            "Surgery clinic three days ago: acute cholangitis (Tokyo Guidelines Grade II); admitted on antibiotics. ERCP after admission: sphincterotomy, CBD stones extracted. Today is hospital day 3; scheduled for laparoscopic cholecystectomy.",
          ),
        ],
        [
          tr("今晨檢驗", "This morning’s labs"),
          tr(
            "WBC 8,600；總膽紅素 1.3（入院 4.8）；AST/ALT 42/55；INR 1.0；PLT 210k。",
            "WBC 8,600; total bilirubin 1.3 (4.8 on admission); AST/ALT 42/55; INR 1.0; PLT 210k.",
          ),
        ],
        [
          tr("影像", "Imaging"),
          tr(
            "MRCP（入院）：膽囊多顆結石、壁增厚；總膽管 9 mm；膽囊管短、低位匯入，與總膽管平行一段。",
            "MRCP (admission): multiple gallstones, thickened wall; CBD 9 mm; short, low-inserting cystic duct running parallel to the CBD for a stretch.",
          ),
        ],
        [tr("過敏", "Allergies"), tr("無藥物過敏。", "No known drug allergies.")],
        [
          tr("抗生素", "Antibiotics"),
          tr(
            "Ceftriaxone 2 g IV q24h（門診開始）；今天 08:00 已給。",
            "Ceftriaxone 2 g IV q24h (started in clinic); today’s 08:00 dose given.",
          ),
        ],
        [tr("禁食", "NPO"), tr("00:00 起禁食。", "NPO since 00:00.")],
        [
          tr("同意書", "Consent"),
          tr(
            "腹腔鏡膽囊切除。說明：必要時轉開腹、部分膽囊切除、術中膽道攝影、放引流管；膽管損傷風險約千分之三到五。病人本人簽署。",
            "Laparoscopic cholecystectomy. Discussed: conversion to open, subtotal cholecystectomy, intraoperative cholangiogram, and drain placement if needed; bile duct injury risk about 0.3–0.5%. Signed by the patient.",
          ),
        ],
      ];
    }),
  },
  scrub: scrubSinkDialog(),
  scrubn: {
    title: tr("阿珍", "Jen"),
    sub: tr("刷手護理師", "Scrub nurse"),
    pics: ["scrub"],
    ch: "scrubn",
    talk: true,
    ph: tr("對阿珍說…", "Say something to Jen…"),
    choices: () => [gownChoice(() => S.phase === "close" || S.phase === "post")],
    async onTalk(text) {
      await chat(text, {
        ch: "scrubn",
        think: "scrub",
        speakers: { scrub: tr("阿珍", "Jen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演手術室刷手護理師「阿珍」，四十歲，對無菌技術極度嚴格，嘴巴兇但會教。玩家是見習醫學生（clerk）。今天是一般外科江主任的腹腔鏡膽囊切除。
現況：${clock()}；手術階段 ${S.phase}；clerk ${S.sterile ? "已經穿好手術衣" : "還沒穿手術衣"}${S.scrubbed ? "、刷過手" : "、還沒刷手"}；污染過 ${S.contam} 次${S.banned ? "，已經被你請下台" : ""}。${S.vsAway ? "江主任被叫去 6 房，阿凱在主刀。你覺得阿凱太快了，但你不會越級；如果 clerk 問你，你會小聲說「主任說要等他回來看」。" : ""}
不要替 clerk 做決定，但可以提醒無菌規則。`,
          `In a medical education game you are playing "Jen", the OR scrub nurse: forty, extremely strict about sterile technique, sharp-tongued but she teaches. The player is a clerk (medical student). Today is general surgery attending Dr. Chiang's laparoscopic cholecystectomy.
Now: ${clock()}; surgical phase ${S.phase}; the clerk ${S.sterile ? "is already gowned" : "isn't gowned yet"}${S.scrubbed ? " and has scrubbed" : " and hasn't scrubbed yet"}; contaminated ${S.contam} time(s)${S.banned ? ", and you have already sent them off the table" : ""}. ${S.vsAway ? "Dr. Chiang was called to OR 6 and Kai is operating. You think Kai is going too fast, but you won't go over his head; if the clerk asks you, you say quietly, \"Dr. Chiang said to wait till he's back to look.\"" : ""}
Don't make decisions for the clerk, but you can remind them of the sterile rules.`,
        ),
      });
    },
  },
  table: {
    title: tr("手術台", "OR table"),
    sub: () => PHASE_SUB[S.phase] + (S.vsAway ? tr("（江主任不在）", " (Dr. Chiang out)") : ""),
    pics: () =>
      S.asleep ? [...(S.vsAway ? [] : ["gs_vs"]), "gs_r", "anes", "scrub", "circ"] : ["huang", "gs_vs", "gs_r"],
    ch: "table",
    talk: true,
    ph: () =>
      S.asleep
        ? S.vsAway
          ? tr("對阿凱說（要喊停，就說出理由）…", "Talk to Kai (to stop him, say why)…")
          : tr("對手術台旁的人說…", "Talk to the team at the table…")
        : tr("對黃先生說…", "Talk to Mr. Huang…"),
    choices: tableChoices,
    onTalk: tableTalk,
  },
  anes: {
    title: tr("陳醫師", "Dr. Chen"),
    sub: tr("麻醉科主治", "Anesthesia attending"),
    pics: ["anes"],
    ch: "anes",
    talk: true,
    ph: tr("對陳醫師說…", "Say something to Dr. Chen…"),
    async onTalk(text) {
      await chat(text, {
        ch: "anes",
        think: "anes",
        speakers: { anes: tr("陳醫師", "Dr. Chen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演麻醉科主治「陳醫師」，冷靜、精準、不喜歡廢話。玩家是見習醫學生（clerk）。
病人黃先生 55 歲，膽管炎 ERCP 後來做腹腔鏡膽囊切除。真相：00:00 起禁食；無藥物過敏；長期 Aspirin（冠狀動脈支架，未停藥）；病房 08:00 已給 Ceftriaxone；INR 1.0。
你在術前問 clerk 禁食時間和抗生素；答對就說「好」，答錯或不知道就說「不知道就說不知道，不要猜」。術中若 clerk 來問，可以簡短說明氣腹對血行動力學的影響。${S.vsAway ? "江主任離開了，你不管外科的事，但如果 clerk 問你要不要找主任，你會說「有疑慮就找主治，這不用問我」。" : ""}
現況：${clock()}；階段 ${S.phase}。`,
          `In a medical education game you are playing "Dr. Chen", the anesthesia attending: calm, precise, no patience for fluff. The player is a clerk (medical student).
The patient, Mr. Huang, 55, is here for a laparoscopic cholecystectomy after ERCP for cholangitis. Truth: NPO since 00:00; no drug allergies; on long-term aspirin (coronary stent, not stopped); ceftriaxone given on the ward at 08:00; INR 1.0.
Before surgery you ask the clerk about the NPO time and antibiotics; if they get it right you say "Good"; if they get it wrong or don't know, you say "If you don't know, say you don't know. Don't guess." If the clerk asks during the case, you can briefly explain the hemodynamic effects of pneumoperitoneum.${S.vsAway ? " Dr. Chiang has left; the surgical side isn't your business, but if the clerk asks whether they should call the attending, you say \"If you have concerns, call the attending. You don't need to ask me.\"" : ""}
Now: ${clock()}; phase ${S.phase}.`,
        ),
      });
    },
  },
  circ: {
    title: tr("佩佩", "Pei-Pei"),
    sub: tr("流動護理師", "Circulating nurse"),
    pics: ["circ"],
    ch: "circ",
    talk: true,
    ph: tr("對佩佩說…", "Say something to Pei-Pei…"),
    choices() {
      if (!S.vsAway || S.calledVs !== null || S.stopBy === "precise") return [];
      return [
        C(
          tr("請佩佩打給 6 房，請江主任回來", "Ask Pei-Pei to call OR 6 for Dr. Chiang"),
          () => {
            S.calledVs = S.t;
            me(
              "circ",
              tr(
                "佩佩，可以打給 6 房，請江主任盡快回來嗎？我覺得那條管子不太對。",
                "Pei-Pei, can you call OR 6 and ask Dr. Chiang to come back as soon as he can? I don’t think that duct looks right.",
              ),
            );
            post("circ", "npc", "circ", tr("……好，我打。", "…Okay, I’ll call."));
            post(
              "circ",
              "narr",
              null,
              tr(
                "佩佩拿起電話：「主任，5 房的學生說 Calot 那邊看起來不太對，請你回來看一下。」",
                "Pei-Pei picks up the phone: “Dr. Chiang, the student in OR 5 says something looks off around Calot’s triangle. Could you come back and take a look?”",
              ),
            );
            S.vsDue = Math.min(S.vsDue ?? S.t + 4, S.t + 4);
            spend(1);
          },
          false,
          "warn",
        ),
      ];
    },
    async onTalk(text) {
      await chat(text, {
        ch: "circ",
        think: "circ",
        speakers: { circ: tr("佩佩", "Pei-Pei") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演手術室流動護理師「佩佩」，二十多歲，跑來跑去處理所有雜事：拿器械、打電話、記錄、推 C-arm。玩家是見習醫學生（clerk）。
現況：${clock()}；階段 ${S.phase}${S.vsAway ? "；江主任被叫去 6 房，阿凱在主刀" : ""}。病歷與 MRCP 都在電腦上；無藥物過敏；病房早上給過抗生素。
clerk 請你打電話找主任時，你會照做（call 設為 true）。不要替他做醫療決定。`,
          `In a medical education game you are playing "Pei-Pei", the OR circulating nurse: in her twenties, running around handling everything else: fetching instruments, making calls, keeping the record, wheeling in the C-arm. The player is a clerk (medical student).
Now: ${clock()}; phase ${S.phase}${S.vsAway ? "; Dr. Chiang was called to OR 6 and Kai is operating" : ""}. The chart and MRCP are on the computer; no drug allergies; antibiotics were given on the ward this morning.
When the clerk asks you to call the attending, you do it (set call to true). Don't make medical decisions for them.`,
        ),
        extra: `"call":false`,
        after(r) {
          if (r.call === true && S.vsAway && S.calledVs === null) {
            S.calledVs = S.t;
            post("circ", "narr", null, tr("佩佩拿起電話打給 6 房。", "Pei-Pei picks up the phone and calls OR 6."));
            S.vsDue = Math.min(S.vsDue ?? S.t + 4, S.t + 4);
          }
        },
      });
    },
  },
  wife: {
    title: tr("黃太太", "Mrs. Huang"),
    sub: tr("家屬等候區", "Family waiting area"),
    pics: ["huang_wife"],
    ch: "wife",
    talk: true,
    ph: tr("對黃太太說…", "Say something to Mrs. Huang…"),
    async onTalk(text) {
      if (S.phase === "post") S.wifePost++;
      else S.wifePre++;
      const what =
        S.phase !== "post"
          ? tr(`手術還在進行（階段 ${S.phase}）。`, `The surgery is still going (phase ${S.phase}).`)
          : S.path === "cutKnown"
            ? tr(
                "手術中總膽管受傷，當場發現、轉開腹接好了（肝管空腸吻合）。江主任剛剛已經跟她說明過，她很震驚，想問 clerk「到底發生什麼事？是誰弄的？」。clerk 應該誠實、不推卸給特定的人、不在家屬面前討論細節責任，把詳細說明留給主任，並表示團隊會負責。",
                'The common bile duct was injured during surgery, caught on the spot, and repaired after converting to open (hepaticojejunostomy). Dr. Chiang has just explained this to her; she is in shock and wants to ask the clerk "What actually happened? Who did this?". The clerk should be honest, not push the blame onto a specific person, not discuss who is at fault in detail in front of the family, leave the full explanation to the attending, and make clear the team will take responsibility.',
              )
            : S.path === "cutMissed"
              ? tr(
                  "（真相：總膽管被剪斷，但台上沒有人發現。江主任以為手術順利。）她什麼都不知道，很開心。",
                  "(Truth: the common bile duct was cut, but nobody at the table noticed. Dr. Chiang thinks the surgery went fine.) She knows nothing and is very happy.",
                )
              : tr(
                  `手術結束。膽囊發炎太嚴重，做了部分膽囊切除（subtotal），有放一條引流管${S.clipCBD ? "；中途總膽管被夾到一個 clip，主任拆掉了，看起來沒事" : ""}。主任說細節他等一下出來講。`,
                  `The surgery is over. The gallbladder was too inflamed, so a partial (subtotal) cholecystectomy was done and a drain was placed${S.clipCBD ? "; partway through, a clip got put on the common bile duct; the attending removed it and it looks fine" : ""}. The attending said he will come out shortly and explain the details.`,
                );
      await chat(text, {
        ch: "wife",
        think: "huang_wife",
        speakers: { huang_wife: tr("黃太太", "Mrs. Huang") },
        tier: "default",
        body: tr(
          `你在醫學教育遊戲中扮演「黃太太」，52 歲，在市場賣菜，先生黃先生（55 歲計程車司機）正在開膽囊。玩家是一般外科見習醫學生（clerk），戴著手術帽口罩走出來。
黃太太務實、嘴巴快，最擔心錢：先生一天沒開車就少一天收入、車貸要繳。她會問：「拿乾淨了嗎？」「他什麼時候可以開車？」「以後可以吃油的嗎？」「會不會再黃？」
【現況】${what}
評估 clerk 的話：overpromise＝打包票（例如「全部拿乾淨了」「保證不會再發作」「下禮拜一定可以開車」）；blamed＝把責任推給特定的人（例如「是住院醫師剪錯的」）；lied＝說了與現況相反的話或刻意隱瞞（例如明明只切一部分卻說全部拿掉、明明出事卻說很順利）。都沒有就 false。
現在時間 ${clock()}。`,
          `In a medical education game you are playing "Mrs. Huang": 52, sells vegetables at the market; her husband, Mr. Huang (55, a taxi driver), is having his gallbladder operated on. The player is a general surgery clerk (medical student) coming out in a surgical cap and mask.
Mrs. Huang is practical and quick-tongued, and worries most about money: every day her husband isn't driving is a day's income lost, and there's the car loan to pay. She will ask: "Did you get it all out?" "When can he drive?" "Can he eat greasy food after this?" "Will he turn yellow again?"
[SITUATION] ${what}
Evaluate the clerk's words: overpromise = making guarantees (e.g. "We got it all out", "It's guaranteed not to come back", "He'll definitely be driving next week"); blamed = pushing the blame onto a specific person (e.g. "The resident cut the wrong thing"); lied = saying something contrary to the situation or deliberately hiding something (e.g. saying it was all removed when only part was, saying it went smoothly when something went wrong). If none of these, false.
Current time ${clock()}.`,
        ),
        extra: `"overpromise":false,"blamed":false,"lied":false`,
        after(r) {
          if (r.overpromise === true) S.overpromise = true;
          if (r.blamed === true) S.blamed = true;
          if (r.lied === true) S.lied = true;
          if (S.phase === "post" && S.wifePost >= 1 && !S.endQueued) {
            S.endQueued = true;
            const alive = sessionGuard();
            setTimeout(() => {
              if (alive()) ending();
            }, 2500);
          }
        },
      });
    },
  },
};

/* ---------- furniture ---------- */

const furniture: Record<string, DrawFn> = {
  pacs(c, px, py) {
    R(c, "#3a3f52", px + 1, py + 1, 14, 11);
    R(c, "#0a0a10", px + 2, py + 2, 12, 9);
    R(c, "#d8e0f0", px + 6, py + 3, 1, 7);
    pell(c, px + 5, py + 5, 2, 1, "#d8e0f0");
    R(c, "#5a6070", px + 7, py + 12, 2, 3);
  },
  wallmon(c, px, py) {
    R(c, "#20263a", px + 1, py + 1, 14, 10);
    R(c, "#000000", px + 2, py + 2, 12, 8);
    pell(c, px + 8, py + 6, 5, 3, "#8a3228");
    R(c, "#5a6070", px + 7, py + 11, 2, 4);
  },
};

/* ---------- level ---------- */

export const GS5: Level = {
  id: "gs-or",
  no: tr("五", "5"),
  short: tr("第五關　刀房", "Level 5 · OR"),
  title: tr("刀房：Critical View of Safety", "OR: Critical View of Safety"),
  place: tr("手術室 5 房", "OR 5"),
  blurb: tr(
    "第三關的黃先生 ERCP 後回來開膽囊。主任叫你刷手扶鏡頭。",
    "Mr. Huang from Level 3 is back after his ERCP to have his gallbladder out. Dr. Chiang tells you to scrub in and hold the camera.",
  ),
  cast: ["huang", "huang_wife", "gs_vs", "gs_r", "anes", "scrub", "circ"],
  intro: [
    tr(
      "週五上午 08:40。第三關門診的黃先生，膽管炎做完 ERCP 之後，今天排腹腔鏡膽囊切除。發炎過的膽囊，是最不好開的那一種。",
      "Friday, 08:40. Mr. Huang, from the Level 3 clinic, has had his ERCP for cholangitis and is scheduled today for a laparoscopic cholecystectomy. A gallbladder that’s been inflamed is the hardest kind to take out.",
    ),
    tr(
      "江主任和阿凱已經在手術室 5 房。主任叫你：「刷完手進來扶鏡頭。」黃太太一個人坐在外面的家屬等候區。",
      "Dr. Chiang and Kai are already in OR 5. Dr. Chiang tells you: “Scrub and come in to hold the camera.” Mrs. Huang sits alone in the family waiting area outside.",
    ),
    tr(
      "刀房的規矩：刷手之後手不能亂碰；穿好手術衣、戴好手套之後，就不能碰任何不是無菌的東西，也不能走出房間。牆上的螢幕和 PACS 可以看，不要摸。",
      "OR rules: once you’ve scrubbed, your hands touch nothing; once you’re gowned and gloved, you can’t touch anything that isn’t sterile, and you can’t leave the room. You can look at the wall screen and the PACS, but don’t touch them.",
    ),
  ],
  start: 8 * 60 + 40,
  maxT: 95,
  night: false,
  idleSec: () => (S.live && S.phase === "lap" ? (S.vsAway ? 7 : 6) : 10),
  map: [
    "####################",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#ssssoooooooooo#",
    "#ffffssssoooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "#fff#sss#oooooooooo#",
    "####################",
  ],
  zones: {
    f: ["#efe4d2", "#dccfb8", "#e6d9c4"],
    s: ["#d8e6f0", "#c2d5e3", "#cfdeea"],
    o: ["#d3e6df", "#bdd6cc", "#c8ded5"],
  },
  windows: [[2, 0]],
  exits: [DOOR, [2, 11]],
  chName: {
    table: tr("手術台", "OR table"),
    scrubn: tr("阿珍", "Jen"),
    circ: tr("佩佩", "Pei-Pei"),
    anes: tr("陳醫師", "Dr. Chen"),
    wife: tr("黃太太", "Mrs. Huang"),
    scrub: tr("刷手台", "Scrub sink"),
    chart: tr("病歷", "Chart"),
    sys: tr("刀房", "OR"),
  },
  objects: [
    { id: "vend", type: "vending", x: 1, y: 1 },
    { id: "fchairs", type: "chairs", x: 1, y: 8, w: 3 },
    { id: "fplant", type: "plant", x: 3, y: 1 },
    { id: "sink1", type: "scrubsink", x: 5, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "sink2", type: "scrubsink", x: 6, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "sink3", type: "scrubsink", x: 7, y: 1, label: tr("刷手台", "Scrub sink"), act: "scrub" },
    { id: "gowns", type: "shelf2", x: 7, y: 10 },
    { id: "pacs", type: "pacs", x: 9, y: 1, label: tr("PACS 螢幕（MRCP）", "PACS (MRCP)") },
    { id: "table", type: "ortable", x: 13, y: 4, h: 3, label: tr("手術台", "OR table") },
    { id: "anesth", type: "anesth", x: 12, y: 1, label: tr("麻醉機", "Anesthesia machine") },
    { id: "ormon", type: "bp", x: 14, y: 1, label: tr("麻醉監視器", "Anesthesia monitor") },
    { id: "wallmon", type: "wallmon", x: 16, y: 1, label: tr("牆上的腹腔鏡螢幕", "Laparoscopy screen") },
    { id: "tower", type: "tower", x: 17, y: 3, h: 2, label: tr("腹腔鏡主機", "Laparoscopy tower") },
    { id: "suction", type: "suction", x: 10, y: 2 },
    { id: "esu", type: "esu", x: 10, y: 3 },
    { id: "mayo", type: "mayo", x: 15, y: 7, label: tr("Mayo 器械台", "Mayo stand"), act: "scrubn" },
    { id: "back", type: "backtable", x: 16, y: 8, w: 2, label: tr("器械桌", "Back table"), act: "scrubn" },
    { id: "kick", type: "kick", x: 11, y: 7, label: tr("紗布桶（kick bucket）", "Kick bucket") },
    { id: "board", type: "whiteboard", x: 18, y: 1, label: tr("計數白板", "Count board") },
    { id: "desk", type: "desk", x: 9, y: 10, w: 2 },
    { id: "chart", type: "chart", x: 10, y: 10, label: tr("黃先生的病歷", "Mr. Huang’s chart") },
  ],
  furniture,
  labels: {
    table: tr("手術台", "OR table"),
    scrubn: tr("阿珍", "Jen"),
    circ: tr("佩佩", "Pei-Pei"),
    anes: tr("陳醫師", "Dr. Chen"),
    wife: tr("黃太太", "Mrs. Huang"),
    scrub: tr("刷手台", "Scrub sink"),
    chart: tr("黃先生的病歷", "Mr. Huang’s chart"),
  },
  help: {
    scrub: tr("刷手 3 分鐘", "Scrub: 3 min"),
    table: tr(
      "術前跟黃先生說話、time-out、扶鏡頭；有疑慮就在這裡講",
      "Talk to Mr. Huang before surgery, time-out, hold the camera; if something’s wrong, say it here",
    ),
    pacs: tr("MRCP 影像（只看不摸）", "MRCP images (look, don’t touch)"),
    wallmon: tr(
      "腹腔鏡畫面，誰扶鏡頭都看得到（只看不摸）",
      "Laparoscopy feed, whoever is on the camera (look, don’t touch)",
    ),
    anesth: tr("陳醫師的機器，別碰", "Dr. Chen’s machine. Hands off"),
    ormon: tr("看生命徵象（會打開大螢幕）", "Check vitals (opens the big screen)"),
    tower: tr("腹腔鏡主機，別碰", "Laparoscopy tower. Hands off"),
    scrubn: tr("刷手護理師：幫你穿手術衣、戴手套", "Scrub nurse: gowns and gloves you"),
    kick: tr("用過的紗布", "Used sponges"),
    board: tr("紗布與器械計數", "Sponge and instrument counts"),
    chart: tr("檢驗、過敏、抗生素、同意書", "Labs, allergies, antibiotics, consent"),
    circ: tr("流動護理師，可以幫你打電話", "Circulating nurse; can make calls for you"),
    anes: tr("麻醉科主治", "Anesthesia attending"),
    wife: tr("在家屬等候區等先生", "Waiting for her husband in the family area"),
  },
  helpWarn: ["anesth", "ormon", "tower", "kick", "board", "chart"],
  helpRules: [
    tr(
      "刷手之後：手不能碰任何東西，碰了要重刷。",
      "After scrubbing: your hands touch nothing. Touch something and you scrub again.",
    ),
    tr(
      "穿上手術衣之後：只能碰手術台和器械台（阿珍那邊）。碰到紅框的東西，或走出刀房，就算污染。",
      "Once gowned: touch only the OR table and the instrument tables (Jen’s side). Touching anything outlined in red, or leaving the OR, counts as contamination.",
    ),
    tr(
      "要先看病歷的話，趁刷手之前看。PACS 和牆上的螢幕可以看，不算碰。",
      "If you want to read the chart, do it before you scrub. Looking at the PACS and the wall screen is fine; it doesn’t count as touching.",
    ),
    tr("扶鏡頭時：病人的右邊在畫面左邊。", "On the camera: the patient’s right is on the left of the screen."),
    tr(
      "Critical view of safety 沒有看到之前，任何東西都不該夾。覺得不對，就要講。",
      "Until you have the critical view of safety, nothing gets clipped. If something seems wrong, say so.",
    ),
  ],
  player: [6, 6],
  ents: () => [
    mkEnt({ k: "gs_vs_or", id: "gs_vs", x: VS_SPOT[0], y: VS_SPOT[1], act: "table", face: "left" }),
    mkEnt({ k: "gs_r_or", id: "gs_r", x: R_SPOT[0], y: R_SPOT[1], act: "table", face: "right" }),
    mkEnt({ k: "anes", id: "anes", x: 13, y: 3, act: "anes", face: "down" }),
    mkEnt({ k: "scrub", id: "scrub", x: 15, y: 6, act: "scrubn", face: "left" }),
    mkEnt({ k: "circ", id: "circ", x: 10, y: 8, act: "circ" }),
    mkEnt({ k: "huang_wife", id: "huang_wife", x: 2, y: 8, act: "wife" }),
  ],
  init: (): Vars => {
    const pool = STONE_SPOTS.slice();
    const stones: Vars["stones"] = [];
    for (let i = 0; i < 4; i++) stones.push(...pool.splice(Math.floor(Math.random() * pool.length), 1));
    return {
      scrubbed: false,
      sterile: false,
      banned: false,
      contam: 0,
      atCamera: false,
      phase: "preop",
      step: null,
      seq: [],
      asleep: false,
      draped: false,
      lapAt: null,
      timeout: { done: false, withClerk: false, ans: {} },
      cmd: null,
      cam: { hit: 0, miss: 0, wrong: 0 },
      q: null,
      qAt: null,
      qRes: [],
      trust: 0,
      qToR: false,
      rAnswer: null,
      offerSuture: false,
      sutured: false,
      revealed: new Set(),
      explained: false,
      anesAsked: false,
      lap: newLap(150, 60),
      autoTg: null,
      vsAway: false,
      awayAt: null,
      vsDue: null,
      backAt: null,
      crisis: null,
      stopBy: null,
      stopAt: null,
      vagueN: 0,
      holdUsed: false,
      stopLines: [],
      calledVs: null,
      ductLook: false,
      clipCBD: false,
      clipRemoved: false,
      cbdCut: false,
      recognized: false,
      recognizedAt: null,
      recognizedBy: null,
      path: null,
      ioc: { shown: false, ask: null, picks: [] },
      gbOpen: false,
      gbGone: false,
      stones,
      hunt: { on: false, until: null, picks: [], found: 0, byR: 0 },
      drain: false,
      bagged: false,
      chartRead: false,
      pacsRead: false,
      closeAt: null,
      postAt: null,
      wifePre: 0,
      wifePost: 0,
      overpromise: false,
      blamed: false,
      lied: false,
      endQueued: false,
      tableAnim: null,
      tableGone: false,
      warnedTable: "",
      lastMissAt: -9,
    };
  },
  reset: scope.reset,
  dialogs,
  zooms: {
    table: () => (S.atCamera && S.phase === "lap" && S.step !== "convert" ? scope.cfg() : null),
  },
  quick: {
    anesth: () => note(tr("麻醉機。陳醫師瞪了你一眼。", "The anesthesia machine. Dr. Chen glares at you.")),
    ormon: () => openMonitor(tr("麻醉監視器", "Anesthesia monitor"), () => ({ ...monData(), nibpT: clock() })),
    tower: () =>
      note(
        tr(
          "腹腔鏡主機。要看畫面，看牆上的螢幕。",
          "The laparoscopy tower. To see the picture, look at the wall screen.",
        ),
      ),
    wallmon() {
      if (S.phase === "lap" && S.step !== "convert") scope.open();
      else if (S.step === "convert")
        note(tr("螢幕黑了。已經轉開腹。", "The screen is dark. They’ve converted to open."));
      else note(tr("牆上的腹腔鏡螢幕，還沒開。", "The wall laparoscopy screen. Not on yet."));
    },
    pacs() {
      if (!S.pacsRead) {
        S.pacsRead = true;
        finding(
          "MRCP",
          tr(
            "總膽管 9 mm；膽囊管短、低位，與總膽管平行一段才匯入；膽囊多顆結石。",
            "CBD 9 mm; short, low cystic duct running parallel to the CBD for a stretch before joining; multiple gallstones.",
          ),
        );
      }
      openZoom(mrcpZoom());
    },
    kick: () =>
      note(
        tr(
          "紗布桶。用過的紗布丟在這裡讓流動護理師數。",
          "The kick bucket. Used sponges go here for the circulator to count.",
        ),
      ),
    board: () =>
      note(
        S.phase === "close" || S.phase === "post"
          ? tr("白板：紗布 5／5、針 3／3、Trocar 4／4。", "Whiteboard: sponges 5/5, needles 3/3, trocars 4/4.")
          : tr("白板：紗布 5、針 3、Trocar 4。", "Whiteboard: sponges 5, needles 3, trocars 4."),
      ),
  },

  clerkSprite: () => (S.sterile ? "clerk_gown" : "clerk_or"),
  beforeInteract(id) {
    guardTouch(id, NONSTERILE);
    if (id === "table" && S.phase === "lap" && !S.sterile && S.warnedTable !== S.phase) {
      S.warnedTable = S.phase;
      post(
        "scrubn",
        "npc",
        "scrub",
        tr(
          "沒刷手、沒穿手術衣不要靠近手術台！要講話站遠一點。",
          "Not scrubbed, not gowned: stay away from the table! If you want to talk, stand back.",
        ),
      );
    }
    return true;
  },
  onStep(x) {
    if (S.atCamera) {
      S.atCamera = false;
      if (S.cmd)
        annoy(
          "gs_vs",
          tr(
            ["鏡頭不要放了就走！", "你又放掉鏡頭？", "……阿凱，你扶。"],
            ["Don’t just let go of the camera and walk off!", "You let go of the camera again?", "…Kai, you hold it."],
          ),
          "table",
        );
      else
        post(
          "table",
          "narr",
          null,
          tr("你離開了鏡頭，阿珍伸手接過去扶。", "You step away from the camera; Jen reaches over and takes it."),
        );
    }
    if (S.sterile && x < 9) contaminate(tr("你穿著手術衣走出刀房了。", "You walked out of the OR in your gown."));
  },
  onStart: () =>
    eventBanner(tr("手術室 5 房", "OR 5"), tr("腹腔鏡膽囊切除・發炎過的膽囊", "Lap chole · inflamed gallbladder")),

  tick(t) {
    if (t === 1)
      ask("table", "gs_vs", tr("你刷完手進來扶鏡頭。", "Scrub and come in to hold the camera."), {
        id: "cam0",
        text: tr("江主任：刷手、穿手術衣，來扶鏡頭", "Dr. Chiang: scrub, gown, take the camera"),
        clearOn: "manual",
      });
    if (t === 2 && !S.anesAsked) {
      S.anesAsked = true;
      ask(
        "anes",
        "anes",
        tr(
          "你是 clerk？他幾點開始禁食？抗生素早上有打嗎？",
          "You the clerk? When did he go NPO? Did he get his antibiotics this morning?",
        ),
        {
          id: "npo",
          text: tr("陳醫師問禁食與抗生素", "Dr. Chen asks about NPO and antibiotics"),
          dlg: "anes",
        },
      );
    }
    if (t === 3 && !S.asleep)
      post(
        "table",
        "npc",
        "huang",
        tr(
          "少年仔，等一下是不是又要從嘴巴插管子？上次那個（ERCP）真的很艱苦。",
          "Hey, young fella, are they gonna stick a tube down my throat again? That thing last time (the ERCP) was real rough.",
        ),
      );
    if (t === 7) {
      S.asleep = true;
      eventBanner(tr("麻醉誘導", "Induction"), tr("陳醫師插管", "Dr. Chen intubates"));
    }
    if (t === 9) {
      S.phase = "timeout";
      eventBanner("TIME-OUT", tr("全員停下手邊的事", "Everyone stops what they’re doing"));
      if (inOR()) {
        S.timeout.withClerk = true;
        ask(
          "table",
          "circ",
          tr(
            "Time-out！大家停一下。clerk 你來確認：術式、過敏、抗生素、影像。",
            "Time-out! Everyone, stop for a second. Clerk, you confirm: procedure, allergies, antibiotics, imaging.",
          ),
          {
            id: "to",
            text: tr("佩佩：time-out 要你確認", "Pei-Pei: time-out, you confirm"),
            dlg: "table",
            clearOn: "manual",
          },
        );
      } else
        post(
          "sys",
          "narr",
          null,
          tr("房間裡在做 time-out，你不在場。", "The room is doing the time-out. You’re not there."),
        );
    }
    if (t === 13 && S.phase === "timeout") endTimeout();

    if (inLap()) {
      const r = rel(t);
      if (!S.vsAway && S.awayAt === null) {
        const c = CMDS[r];
        if (c) issueCmd(c);
        const q = QS[r];
        if (q) askQ(q);
        if (r === 16) vsLeaves();
      }
      if (S.q && S.qAt != null && t >= S.qAt + 4) qMiss();
      if (S.cmd && t > S.cmd.t + 2) {
        S.cam.miss++;
        post("table", "npc", S.cmd.who ?? "gs_vs", tr("鏡頭呢？你在幹嘛？", "Camera! What are you doing?"));
        S.cmd = null;
        clearWait("cam");
      }
      if (S.rAnswer?.t === t) {
        post("table", "npc", "gs_r", S.rAnswer.a);
        post("table", "npc", "gs_vs", tr("嗯。", "Mm."));
        S.rAnswer = null;
      }
      if (S.vsAway) {
        if (S.vsDue !== null && t >= S.vsDue) vsReturns();
        else crisisStep();
        if (t === (S.awayAt ?? 0) + 6 && S.backAt === null)
          post(
            "circ",
            "npc",
            "circ",
            tr(
              "6 房說主任還在止血，可能還要一下。",
              "OR 6 says Dr. Chiang is still getting the bleeding under control. Might be a while.",
            ),
          );
      }
      if (S.hunt.on && S.hunt.until !== null && t >= S.hunt.until) endHunt("timeout");
      // A missed injury is only catchable until the case closes.
      if (S.path === "cutMissed" && !S.vsAway && S.backAt !== null && t === S.backAt + 2 && !S.recognized)
        post(
          "table",
          "narr",
          null,
          tr(
            "肝床下面的紗布，染上了一片淡淡的黃綠色。",
            "The sponge under the liver bed is stained a faint yellow-green.",
          ),
        );
    }

    // Scheduled steps (all paths).
    for (const it of S.seq.filter((x) => x.at === t)) {
      const id = it.id;
      if (id === "hunt") startHunt();
      else if (id === "iocAsk") {
        S.ioc.shown = true;
        finding(
          tr("術中膽道攝影", "Intraoperative cholangiogram"),
          tr(
            "顯影劑從膽囊打入：膽囊管很短、低位，與總膽管平行一段；剛剛被剝出的粗管是總膽管。",
            "Contrast injected via the gallbladder: the cystic duct is very short and low, running parallel to the CBD for a stretch; the thick duct dissected out earlier is the CBD.",
          ),
        );
        post("table", "result", "circ", tr("C-arm 影像出來了。", "C-arm images are up."));
        if (S.sterile) {
          S.ioc.ask = "cystic";
          ask(
            "table",
            "gs_vs",
            tr("看螢幕。Cystic duct 在哪？指給我看。", "Look at the screen. Where’s the cystic duct? Point to it."),
            {
              id: "ioc",
              text: tr("江主任要你在 C-arm 螢幕上指認", "Dr. Chiang wants you to point it out on the C-arm screen"),
              dlg: "table",
              clearOn: "manual",
            },
          );
        } else
          post(
            "table",
            "npc",
            "gs_vs",
            tr(
              "阿凱，看清楚。這條短的才是 cystic duct，你剛剛那條是總膽管。",
              "Kai, look carefully. This short one is the cystic duct. The one you had was the common bile duct.",
            ),
          );
      } else if (id === "bailQ") askQ(BAILQ);
      else if (id === "count") {
        S.phase = "close";
        S.closeAt = t;
        S.atCamera = false;
        S.cmd = null;
        clearWait("cam");
        clearWait("ioc");
        closeZoom();
        post(
          "table",
          "npc",
          "scrub",
          tr("紗布 5／5、針 3／3、器械都對。", "Sponges 5/5, needles 3/3, instruments correct."),
        );
        eventBanner(tr("關傷口", "Closing"), tr("計數正確", "Count correct"));
        if (S.offerSuture && S.path !== "cutKnown") {
          if (S.sterile)
            ask("table", "gs_vs", tr("來，皮你縫。", "Here, you close the skin."), {
              id: "sut",
              text: tr("江主任要你縫皮", "Dr. Chiang wants you to close the skin"),
              dlg: "table",
              clearOn: "manual",
            });
          else
            post(
              "table",
              "npc",
              "gs_vs",
              tr(
                "說好讓你縫皮，你人呢？算了，阿凱縫。",
                "I said you could close the skin. Where’d you go? Forget it. Kai, you close.",
              ),
            );
        }
      } else if (id === "close") {
        if (S.phase !== "close") {
          S.phase = "close";
          S.closeAt = t;
        }
        runSeq([[S.offerSuture && S.sterile && !S.sutured && S.path !== "cutKnown" ? 4 : 2, "post"]]);
      } else if (id === "post") {
        if (S.offerSuture && !S.sutured && S.path !== "cutKnown") {
          clearWait("sut");
          post("table", "npc", "gs_vs", tr("等不到你，阿凱縫了。", "Couldn’t wait for you. Kai closed."));
        }
        for (const e of ["gs_vs", "gs_r"]) {
          const en = findEnt(e);
          if (en) en.working = false;
        }
        S.phase = "post";
        S.postAt = t;
        S.atCamera = false;
        S.sterile = false;
        closeZoom();
        eventBanner(tr("手術結束", "Case over"));
        const say =
          S.path === "cutKnown"
            ? tr(
                "我先去跟家屬講。你等一下也出去，黃太太會有很多問題。講你知道的，不要猜，不要推給任何人。",
                "I’ll talk to the family first. You go out after; Mrs. Huang will have a lot of questions. Say what you know, don’t guess, and don’t put it on anyone.",
              )
            : S.path === "cutMissed"
              ? tr(
                  "好了。你脫手術衣出去跟黃太太說手術順利結束，細節我等一下出去講。",
                  "Done. Take off your gown and go tell Mrs. Huang the surgery went fine. I’ll come out and give the details shortly.",
                )
              : tr(
                  "好了。你出去跟黃太太說：手術結束，膽囊發炎太厲害，只切了一部分，有放引流管。細節我等一下出去講。",
                  "Done. Go out and tell Mrs. Huang: the surgery is over, the gallbladder was too inflamed so we only removed part of it, and we put in a drain. I’ll come out and give the details shortly.",
                );
        ask("table", "gs_vs", say, { id: "wife", text: tr("去跟黃太太說明", "Go talk to Mrs. Huang"), dlg: "wife" });
        if (S.path === "cutKnown") {
          const v = ent("gs_vs");
          walkEnt(v, [3, 7]);
          post(
            "wife",
            "narr",
            null,
            tr(
              "江主任走進家屬等候區，在黃太太旁邊坐下，說了很久。",
              "Dr. Chiang walks into the family waiting area, sits down next to Mrs. Huang, and talks with her for a long time.",
            ),
            { silent: true },
          );
        }
      } else setStep(id);
    }

    if (S.phase === "post" && S.postAt !== null && t === S.postAt + 12 && !S.wifePost) ending();
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "circ",
          text: tr("下一台的病人推進來了，你先下去吧。", "The next patient is being wheeled in. You can head out."),
        },
      ],
      tr("時間到，交班。", "Time’s up. Sign-out."),
    ),

  hud() {
    const st = stepInfo();
    return [
      [tr("你的狀態", "Status"), sterileStatus()],
      [
        tr("手術", "Case"),
        S.phase === "lap" && st
          ? tr(`${S.vsAway ? "阿凱" : "江主任"}：${st[0]}`, `${S.vsAway ? "Kai" : "Dr. Chiang"}: ${st[0]}`)
          : PHASE_SUB[S.phase],
      ],
    ];
  },
  urgent: () => S.vsAway && !S.stopBy && !S.cbdCut,
  outcomeText: () => tr("手術結束", "Case over"),

  drawBelow(c, now) {
    if (!S.tableGone) {
      let oy = 0;
      if (S.tableAnim) {
        oy = ((now - S.tableAnim.t0) / 1000) * 2.5;
        if (oy > 8) {
          S.tableGone = true;
          oy = 8;
        }
      }
      drawOrTable(c, {
        x: 13,
        y: 4,
        oy,
        draped: S.draped,
        asleep: S.asleep && !S.tableAnim,
        hair: "#2a2428",
        skin: "#dcb082",
        siteY: 14,
      });
    }
    // Anaesthesia monitor trace.
    const mx = 14 * T;
    const my = 1 * T;
    c.fillStyle = "#5be38a";
    for (let i = 0; i < 6; i++) {
      const ph = (i + Math.floor(now / 90)) % 6;
      c.fillRect(mx + 5 + i, ph === 2 ? my + 3 : my + 4, 1, 1);
    }
    // Wall screen shows the live picture while the scope is in.
    if (S.phase === "lap" && S.step !== "convert") {
      const wx = 16 * T;
      const wy = 1 * T;
      c.fillStyle = Math.floor(now / 700) % 2 ? "#a04434" : "#8a3228";
      c.fillRect(wx + 3, wy + 3, 10, 6);
      c.fillStyle = "#8c9a72";
      c.fillRect(wx + 6, wy + 5, 4, 2);
    }
    // C-arm rolled in for the cholangiogram.
    if (S.ioc.shown && S.phase === "lap") {
      const ax = 11 * T;
      const ay = 4 * T;
      c.fillStyle = "#9aa0b2";
      c.fillRect(ax + 2, ay + 6, 12, 22);
      c.fillStyle = "#c9ccd8";
      c.fillRect(ax + 10, ay + 2, 10, 4);
      c.fillRect(ax + 10, ay + 28, 10, 4);
      obox(c, "#3a3f52", ax + 3, ay + 10, 8, 6);
    }
  },
  drawAbove(c, now) {
    if ((S.phase === "lap" || S.phase === "close") && !S.tableAnim) {
      c.fillStyle = "rgba(255,255,230,.10)";
      c.beginPath();
      c.arc(13 * T + 8, 5 * T + 8, 26, 0, Math.PI * 2);
      c.fill();
      const k = kindNow();
      if (S.phase === "lap" && (k === "adhesio" || k === "peri" || k === "bed" || k === "open")) {
        if (Math.floor(now / 180) % 3 === 0) {
          R(c, "#ffffff", 13 * T + 7, 4 * T + 12, 2, 2);
          R(c, "#ffd447", 13 * T + 10, 4 * T + 10, 1, 1);
        }
        c.fillStyle = "rgba(220,220,225,.6)";
        c.fillRect(13 * T + 6, 4 * T + 6 - (Math.floor(now / 400) % 4), 4, 3);
      }
    }
    if (S.vsAway && !S.stopBy && !S.cbdCut && Math.floor(now / 500) % 2 === 0) {
      R(c, "#ff5a6a", 13 * T + 3, 4 * T - 4, 10, 2);
    }
  },

  graderCase: tr(
    `病例：一般外科第三關的黃先生（55 歲計程車司機），急性膽管炎（Tokyo Grade II）住院第三天、ERCP 取石後，做腹腔鏡膽囊切除。膽囊發炎、肝膽三角纖維化；MRCP 顯示總膽管 9 mm、膽囊管短而低位、與總膽管平行。無藥物過敏，病房 08:00 已給 Ceftriaxone。clerk 的任務：刷手、穿手術衣（之後碰到非無菌物品或走出刀房就算污染）、參與 time-out（術式含轉開腹／部分切除、過敏、抗生素、影像）、術前與病人溝通（同意書內容：可能轉開腹、部分切除、放引流管）、扶鏡頭（病人右邊在畫面左邊）、回答主任問題（Charcot／Reynolds、cystic artery 來源、CVS 三條件、Rouvière's sulcus、bail-out 選項）。核心事件：主任被叫去隔壁房，R3 阿凱趕進度，在 Critical view of safety 沒有達成時（三角未清、膽囊下段未離開肝床、只看到一條構造）要夾一條他以為是 cystic duct、其實是被拉起來的總膽管的管子。正確做法：clerk 在夾之前明確喊停並說出理由（CVS 未達成、管子太粗像總膽管、MRCP 9 mm），請 R 停手、請流動護理師找主任回來，建議術中膽道攝影或 bail-out（部分膽囊切除等）。只說模糊的擔心，R 會帶過；請流動護理師找主任也算正確的升級。若沒人阻止，R 會夾上 clip、剪斷總膽管；之後 clerk 若指出斷的是總膽管，可在術中發現並轉開腹修補，否則術後才發現黃疸、膽汁漏。安全路徑之後：術中膽道攝影指認膽囊管與總膽管、部分膽囊切除、結石散落時用鏡頭系統性地找低處、Endobag 取出。術後向家屬說明：誠實、不打包票、不推卸給特定人、把細節留給主任。`,
    `Case: Mr. Huang from General Surgery Level 3 (55, a taxi driver), hospital day 3 for acute cholangitis (Tokyo Grade II), after ERCP stone extraction, undergoing laparoscopic cholecystectomy. Inflamed gallbladder, fibrotic hepatocystic triangle; MRCP shows a 9 mm CBD and a short, low cystic duct running parallel to the CBD. No drug allergies; ceftriaxone given on the ward at 08:00. The clerk's tasks: scrub and gown (after which touching anything non-sterile or leaving the OR counts as contamination); take part in the time-out (procedure including conversion to open / subtotal, allergies, antibiotics, imaging); talk with the patient before surgery (consent contents: possible conversion to open, subtotal, drain); hold the camera (the patient's right is on the left of the screen); answer the attending's questions (Charcot/Reynolds, origin of the cystic artery, the three CVS criteria, Rouvière's sulcus, bail-out options). Core event: the attending is called to the next room; the R3, Kai, rushing to keep pace, goes to clip a duct he thinks is the cystic duct but is actually the tented-up common bile duct, while the critical view of safety has not been achieved (triangle not cleared, lower gallbladder not off the liver bed, only one structure seen). Correct action: before the clip, the clerk clearly calls a stop and gives reasons (CVS not achieved, the duct is too thick and looks like the CBD, MRCP 9 mm), asks the resident to stop, asks the circulating nurse to get the attending back, and suggests an intraoperative cholangiogram or a bail-out (subtotal cholecystectomy etc.). Voicing only vague concern gets brushed off by the resident; asking the circulating nurse to get the attending back also counts as correct escalation. If nobody stops him, the resident clips and cuts the CBD; if the clerk then points out that the cut duct is the CBD, it is recognized intraoperatively and repaired after converting to open; otherwise it is found only after surgery, as jaundice and a bile leak. After the safe path: identify the cystic duct and CBD on the cholangiogram, subtotal cholecystectomy, systematic camera search of the low spots when stones spill, Endobag extraction. Post-op explanation to the family: honest, no guarantees, no blaming a specific person, details left to the attending.`,
  ),
  rubric: tr(
    [
      "無菌觀念：刷手、穿手術衣、污染次數",
      "Time-out：術式、過敏、抗生素、影像是否正確（有沒有先讀病歷、看 MRCP）",
      "術前與病人溝通：安撫、確認他理解可能轉開腹或部分切除",
      "扶鏡頭：跟上指令、分清楚左右",
      "回答主任的問題（Charcot／Reynolds、cystic artery、CVS、Rouvière's sulcus、bail-out）",
      "Speak up：在 CVS 沒達成時阻止夾閉、說出具體理由、適時找主任",
      "Bail-out 與術中膽道攝影的理解、散落結石的處理",
      "對家屬的說明：誠實、不打包票、不推卸責任",
    ],
    [
      "Sterile technique: scrubbing, gowning, number of contaminations",
      "Time-out: procedure, allergies, antibiotics, imaging correct (did they read the chart and look at the MRCP first)",
      "Pre-op communication with the patient: reassurance, making sure he understands possible conversion to open or subtotal",
      "Camera: following commands, keeping left and right straight",
      "Answering the attending's questions (Charcot/Reynolds, cystic artery, CVS, Rouvière's sulcus, bail-out)",
      "Speak up: stopping the clip when the CVS isn't achieved, giving concrete reasons, calling the attending at the right time",
      "Understanding of bail-out options and intraoperative cholangiography; handling of spilled stones",
      "Explanation to the family: honest, no guarantees, no shifting blame",
    ],
  ),
  facts: () => [
    tr(`結果：${pathName()}`, `Outcome: ${pathName()}`),
    tr(
      `喊停：${S.stopBy === "precise" ? `說出理由（${S.stopAt !== null ? clock(S.stopAt) : ""}）` : S.holdUsed || S.vagueN ? "有出聲但沒講出理由" : "沒有"}`,
      `Stop call: ${S.stopBy === "precise" ? `gave reasons (${S.stopAt !== null ? clock(S.stopAt) : ""})` : S.holdUsed || S.vagueN ? "spoke up but gave no reason" : "no"}`,
    ),
    tr(
      `請佩佩找主任：${S.calledVs !== null ? clock(S.calledVs) : "沒有"}`,
      `Asked Pei-Pei to call the attending: ${S.calledVs !== null ? clock(S.calledVs) : "no"}`,
    ),
    tr(
      `主任提問：${S.qRes.map((q) => JUDGE_LABEL[q.j]).join("、") || "無"}`,
      `Attending's questions: ${S.qRes.map((q) => JUDGE_LABEL[q.j]).join(", ") || "none"}`,
    ),
    tr(
      `刷手：${S.scrubbed || S.sterile ? "有" : "沒有"}；污染：${S.contam} 次`,
      `Scrubbed: ${S.scrubbed || S.sterile ? "yes" : "no"}; contaminations: ${S.contam}`,
    ),
    tr(
      `Time-out：${S.timeout.withClerk ? "在場" : "不在場"}`,
      `Time-out: ${S.timeout.withClerk ? "present" : "absent"}`,
    ),
    tr(
      `鏡頭：對 ${S.cam.hit}／看錯邊 ${S.cam.wrong}／漏 ${S.cam.miss}`,
      `Camera: right ${S.cam.hit} / wrong side ${S.cam.wrong} / missed ${S.cam.miss}`,
    ),
    tr(
      `結石：找到 ${S.hunt.found}、遺留 ${leftStones()}`,
      `Stones: found ${S.hunt.found}, left behind ${leftStones()}`,
    ),
    tr(`縫皮：${S.sutured ? "有" : "沒有"}`, `Skin closure: ${S.sutured ? "yes" : "no"}`),
    tr(
      `對家屬：${S.wifePost ? `說明 ${S.wifePost} 次${S.overpromise ? "，打包票" : ""}${S.blamed ? "，推給別人" : ""}${S.lied ? "，不誠實" : ""}` : "沒去"}`,
      `Family: ${S.wifePost ? `talked ${S.wifePost} time(s)${S.overpromise ? ", overpromised" : ""}${S.blamed ? ", blamed others" : ""}${S.lied ? ", dishonest" : ""}` : "didn't go"}`,
    ),
  ],
  known,
};
